import React, { useEffect, useMemo, useState, Children, Fragment, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ExternalLinkIcon, LogOutIcon } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, signOut, User } from 'firebase/auth';
import { getFirestore, collection, query, orderBy, onSnapshot, addDoc, updateDoc, setDoc, doc, getDoc, deleteDoc, arrayUnion, serverTimestamp, Timestamp } from 'firebase/firestore';
// ── Firebase Initialization ─────────────────────────────────────
const firebaseConfig = {
  apiKey: 'AIzaSyC95YAd0hECiLzZYyLj3kAf8fIjDRo6mvg',
  authDomain: 'gsfliveagency-31e40.firebaseapp.com',
  projectId: 'gsfliveagency-31e40',
  storageBucket: 'gsfliveagency-31e40.firebasestorage.app',
  messagingSenderId: '1023743622410',
  appId: '1:1023743622410:web:611f17b3063fd1c86c8ef3',
  measurementId: 'G-LBQC232NCB'
};
const app = initializeApp(firebaseConfig, 'agency');
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const creatorFirebaseConfig = {
  apiKey: 'AIzaSyC95YAd0hECiLzZYyLj3kAf8fIjDRo6mvg',
  authDomain: 'gsfliveagency-31e40.firebaseapp.com',
  projectId: 'gsfliveagency-31e40',
  storageBucket: 'gsfliveagency-31e40.firebasestorage.app',
  messagingSenderId: '1023743622410',
  appId: '1:1023743622410:web:9d703a73017f16676c8ef3',
  measurementId: 'G-LYZ8QPBFTZ'
};
const creatorApp = initializeApp(creatorFirebaseConfig, 'agency-creator');
// Safe helper to convert Firestore Timestamps, plain objects, or other date-like values to Date
function safeToDate(val: any): Date | null {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  if (typeof val.seconds === 'number') return new Date(val.seconds * 1000);
  if (typeof val === 'number') return new Date(val);
  if (typeof val === 'string') return new Date(val);
  return null;
}
const creatorAuth = getAuth(creatorApp);
const creatorDb = getFirestore(creatorApp);
function ActionButtons({
  currentPage,
  onNavigate
}: {
  currentPage: PageId;
  onNavigate: (page: PageId) => void;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.8,
    duration: 0.5
  }} className="mt-16 mb-10 flex flex-col items-center justify-center w-full" data-id="element-544" data-mp="2e6wnxgk9ld8g">
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-545" data-mp="1zap6go08qpwv">
        <motion.a href="mailto:trippinwithmandy@gmail.com" whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} className="inline-flex items-center px-6 py-3 rounded-xl border border-white/50 bg-transparent text-white font-semibold font-['Montserrat'] hover:bg-white/10 transition-all" data-id="element-546" data-mp="1uhcsr8x3o3iw">
          Contact Us
        </motion.a>
        {currentPage !== 'contact' && <motion.a href="https://www.tiktok.com/t/ZTUegUbeP/" target="_blank" rel="noopener noreferrer" whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} className="inline-flex items-center px-6 py-3 rounded-xl border border-white/50 bg-transparent text-white font-semibold font-['Montserrat'] hover:bg-white/10 transition-all" data-id="element-547" data-mp="ipdl16rx0kxp">
            Join TWM
          </motion.a>}
      </div>
    </motion.div>;
}
type PageId = 'home' | 'about' | 'why' | 'services' | 'contact' | 'agent-login' | 'creator-login' | 'portal-hub' | 'creator-hub' | 'portal' | 'creator-portal';
const NAV_ITEMS: {
  label: string;
  page: PageId;
}[] = [{
  label: 'Home',
  page: 'home'
}, {
  label: 'About Us',
  page: 'about'
}, {
  label: 'Why TWM',
  page: 'why'
}, {
  label: 'Services',
  page: 'services'
}, {
  label: 'Join',
  page: 'contact'
}];
// ── Org Chart Data ──────────────────────────
interface OrgNode {
  id: string;
  name: string;
  title: string;
  photo?: string;
  tiktok?: string;
  size: 'owner' | 'director' | 'agent' | 'creator';
  children?: OrgNode[];
}
const ORG_DATA: OrgNode = {
  id: 'owner',
  name: 'STARPLAYER757',
  title: 'TWM Owner / Founder',
  photo: 'https://cdn.magicpatterns.com/uploads/v5aPbSvNA9zHB77CtwRwnd/file_000000007cbc71fb950c62ed89b90586.png',
  tiktok: 'https://www.tiktok.com/@zoeaking?_r=1&_t=ZP-95d2CTbttLr',
  size: 'owner',
  children: [{
    id: 'co-lead',
    name: '"Q" BigAFrememberdat',
    title: 'Assistant to CEO / Co-Lead Agent',
    photo: 'https://cdn.magicpatterns.com/uploads/ugsYfBkVtUZWuxXTsbnVba/file_00000000e44871fb959602b9223382bc.png',
    tiktok: 'https://www.tiktok.com/@bigafrememberdat',
    size: 'director',
    children: [{
      id: 'dir-1',
      name: 'Trippin.with.mandy',
      title: 'Director of Coordination',
      photo: 'https://cdn.magicpatterns.com/uploads/azExE3PWnnQgeGkusYujhB/ChatGPT_Image_May_12,_2026,_04_22_46_PM.png',
      tiktok: 'https://www.tiktok.com/@trippin.with.mandy',
      size: 'director',
      children: [{
        id: 'agt-1',
        name: 'STARPLAYER757',
        title: 'Lead Agent',
        photo: 'https://cdn.magicpatterns.com/uploads/v5aPbSvNA9zHB77CtwRwnd/file_000000007cbc71fb950c62ed89b90586.png',
        tiktok: 'https://www.tiktok.com/@zoeaking?_r=1&_t=ZP-95d2CTbttLr',
        size: 'agent',
        children: [{
          id: 'crt-1',
          name: 'NESSY™',
          title: 'Live Creator',
          photo: 'https://cdn.magicpatterns.com/uploads/sqa959qSCN7fRwwSGBVycr/ChatGPT_Image_Apr_22,_2026,_10_10_18_PM.png',
          tiktok: 'https://www.tiktok.com/@nessytherilla',
          size: 'creator'
        }, {
          id: 'crt-tommy',
          name: 'DLS tommy TWM',
          title: 'Live Creator',
          photo: 'https://cdn.magicpatterns.com/uploads/52573FpnMNpTtPbKruniGD/file_00000000d22c71f5ba9cea1c3bee59cc.png',
          tiktok: 'https://www.tiktok.com/@dlstommy12',
          size: 'creator'
        }]
      }, {
        id: 'agt-2',
        name: 'Trippin.with.mandy',
        title: 'TWM Agent',
        photo: 'https://cdn.magicpatterns.com/uploads/azExE3PWnnQgeGkusYujhB/ChatGPT_Image_May_12,_2026,_04_22_46_PM.png',
        tiktok: 'https://www.tiktok.com/@trippin.with.mandy',
        size: 'agent',
        children: [{
          id: 'crt-3',
          name: 'ZettaMINDset',
          title: 'Live Creator',
          photo: 'https://cdn.magicpatterns.com/uploads/mqTALxa5F64M2yQL8KH6YU/file_0000000036f871fdb97ff10845e446c2.png',
          tiktok: 'https://www.tiktok.com/@mszetta?_r=1&_t=ZP-95d2AQ9iXwU',
          size: 'creator'
        }]
      }, {
        id: 'agt-4',
        name: 'J3',
        title: 'TWM Agent',
        photo: 'https://cdn.magicpatterns.com/uploads/kHPVAQbDpnt8Zdza5GNRAF/file_00000000dfc071fdaf303e4b92a454de.png',
        tiktok: 'https://www.tiktok.com/@chyyy..0110?_r=1&_t=ZP-95d27IRtmj1',
        size: 'agent',
        children: [{
          id: 'crt-5',
          name: 'IAM_JDOLLA',
          title: 'Live Creator',
          photo: 'https://cdn.magicpatterns.com/uploads/m4k4ZRiRRre616KQMVQBsA/ChatGPT_Image_Apr_22,_2026,_10_17_55_PM.png',
          tiktok: 'https://www.tiktok.com/@jdolla_74?_r=1&_t=ZP-95d1zT2i8L9',
          size: 'creator'
        }]
      }]
    }]
  }]
};
// ── Org Chart Components ───────────────────────────────────────────
function getInitials(name: string) {
  const clean = name.replace(/[^a-zA-Z0-9 ]/g, ' ').trim();
  const parts = clean.split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return clean.substring(0, 2).toUpperCase();
}
function OrgCard({
  node
}: {
  node: OrgNode;
}) {
  const imageSizes = {
    owner: 'w-44 md:w-72',
    director: 'w-36 md:w-60',
    agent: 'w-32 md:w-52',
    creator: 'w-28 md:w-44'
  };
  return <motion.a href={node.tiktok || '#'} target={node.tiktok ? '_blank' : '_self'} rel={node.tiktok ? 'noopener noreferrer' : ''} initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} whileHover={{
    scale: 1.08,
    y: -5
  }} className={`flex flex-col items-center cursor-pointer group ${imageSizes[node.size]}`} data-id="element-548" data-mp="3p5ax4v1t6n54">
      {node.photo ? <img src={node.photo} alt={node.name} className="object-contain" data-id="element-549" data-mp="3bctlobuxkbg1" /> : <div className="w-full aspect-square bg-gradient-to-br from-purple-600 to-blue-600 flex flex-col items-center justify-center p-3 rounded-full" data-id="element-550" data-mp="3gcppob3mo2vl">
          <span className="text-white font-bold text-lg md:text-2xl tracking-wider" data-id="element-551" data-mp="2npv93p9xlnne">
            {getInitials(node.name)}
          </span>
          <span className="text-white/80 text-[8px] md:text-xs mt-1 text-center font-medium" data-id="element-552" data-mp="2smvfk1ap0sf1">
            {node.name}
          </span>
          <span className="text-white/50 text-[7px] md:text-[10px] text-center" data-id="element-553" data-mp="18fn75rhdh973">
            {node.title}
          </span>
        </div>}
    </motion.a>;
}
function TreeNode({
  node,
  isRoot = false,
  isFirst = false,
  isLast = false,
  isOnly = false
}: {
  node: OrgNode;
  isRoot?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isOnly?: boolean;
}) {
  return <div className="flex flex-col items-center relative px-1 md:px-3" data-id="element-554" data-mp="krvs4g4g6i24">
      {/* Lines above the node (skip for root) */}
      {!isRoot && <>
          {/* Horizontal line connecting siblings */}
          {!isOnly && <div className={`absolute top-0 h-[2px] bg-gradient-to-r from-purple-500/60 to-blue-500/60
              ${isFirst ? 'left-1/2 right-0' : isLast ? 'left-0 right-1/2' : 'left-0 right-0'}
            `} data-id="element-555" data-mp="yh7hzu1woa08" />}
          {/* Vertical line down to card */}
          <div className="w-[2px] h-4 md:h-8 bg-gradient-to-b from-purple-500/60 to-blue-500/60" data-id="element-556" data-mp="3umrp31jbeoli" />
        </>}
      {/* The Card */}
      <OrgCard node={node} data-id="element-557" data-mp="3pp8c04ycshv5" />
      {/* Lines below the node (if has children) */}
      {node.children && node.children.length > 0 && <>
          {/* Vertical line down from card */}
          <div className="w-[2px] h-4 md:h-8 bg-gradient-to-b from-purple-500/60 to-blue-500/60" data-id="element-558" data-mp="3gmgz00ptxe1e" />
          {/* Children container */}
          <div className="flex justify-center items-start relative" data-id="element-559" data-mp="3bq0b97mjl1ou">
            {node.children.map((child, idx) => <TreeNode key={child.id} node={child} isRoot={false} isFirst={idx === 0} isLast={idx === node.children!.length - 1} isOnly={node.children!.length === 1} data-id="element-560" data-mp="31y2w5psexpku" data-mpi={idx} />)}
          </div>
        </>}
    </div>;
}
// ── Meet the Team Page ─────────────────────────────────────────
function MeetTheTeam() {
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-561" data-mp="2ea5z583gyflf">
      {/* Section Title */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8
    }} className="text-center mb-12" data-id="element-562" data-mp="2jbn2rbdc8g5p">
        <h2 className="font-['Montserrat'] font-black text-3xl md:text-5xl tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-purple-300 uppercase" data-id="element-563" data-mp="2x0x8i9ngi5zt">
          TWM Agency Chain of Command
        </h2>
        <p className="text-white/50 mt-3 text-sm md:text-base max-w-md mx-auto" data-id="element-564" data-mp="2navrg1titnue">
          Tap a card to visit their TikTok profile.
        </p>
      </motion.div>
      {/* Highlight Reel */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.3
    }} className="text-center mb-16" data-id="element-565" data-mp="3fzj9zpmvxz49">
        <h3 className="font-['Montserrat'] font-black text-2xl md:text-3xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-8" data-id="element-566" data-mp="14dr4h49rdog8">
          🎬 Highlight Reel
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-7xl mx-auto px-2 md:px-4" data-id="element-567" data-mp="1e2isx2fm1qme">
          {[
        // ── Highlight Video 1 — swap this URL anytime ──
        'https://cdn.magicpatterns.com/uploads/j2L5HFDMJk71ujTNf9VgKn/05b56bc761ac1841cee144bc2624aa30.mp4',
        // ── Highlight Video 2 — swap this URL anytime ──
        'https://cdn.magicpatterns.com/uploads/nca79a8eKdsccyD2PqVw67/9b1f930f693ec19f3966b3e14bc46d32.mp4',
        // ── Highlight Video 3 — swap this URL anytime ──
        'https://cdn.magicpatterns.com/uploads/m8WkUNUJey2Xborr1k9m9c/8e4b4ec9eaf3df3cb2d789f280e7966e.mp4',
        // ── Highlight Video 4 — swap this URL anytime ──
        'https://cdn.magicpatterns.com/uploads/snU4LBjHSricg2jA9pMfNX/77ed4a9232548fb2569a2d5f0cdbd790.mp4'].map((url, i) => <motion.div key={i} initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.5 + i * 0.15,
          duration: 0.4
        }} className="w-full rounded-2xl overflow-hidden backdrop-blur-md bg-black/10 border border-purple-500/30 shadow-[0_0_20px_rgba(168,85,247,0.2)]" data-id="element-568" data-mp="365aunbjkxiud" data-mpi={i}>
              <video src={url} controls className="w-full h-auto aspect-[9/16] object-cover" preload="metadata" data-id="element-569" data-mp="2m8183zh0388" />
            </motion.div>)}
        </div>
      </motion.div>
      {/* Org Chart / Family Tree */}
      <div className="w-full overflow-x-auto pb-10 scrollbar-thin scrollbar-thumb-purple-500/50 scrollbar-track-transparent" data-id="element-570" data-mp="1x2g90l6qls32">
        <div className="min-w-max mx-auto flex justify-center px-4" data-id="element-571" data-mp="1s2lhcff50ygy">
          <TreeNode node={ORG_DATA} isRoot={true} data-id="element-572" data-mp="1n4a7w5sr8pz9" />
        </div>
      </div>
    </div>;
}
// ── Live Schedules Page ──────────────────────────────────────
function LiveSchedules({
  onNavigate
}: {
  onNavigate: (page: PageId) => void;
}) {
  const categories = [{
    id: 'engagement',
    title: 'Engagement Lives',
    emoji: '🎯',
    agents: [{
      name: 'ZettaMINDset',
      page: 'schedule-zetta' as PageId
    }]
  }, {
    id: 'music',
    title: 'Music Related Lives',
    emoji: '🎵',
    agents: [{
      name: "Star's Pad Lounge",
      page: 'schedule-star-dj' as PageId
    }, {
      name: 'Nessy',
      page: 'schedule-nessy' as PageId
    }]
  }, {
    id: 'coaching',
    title: 'Coaching Lives',
    emoji: '🧠',
    agents: [{
      name: 'Trippin with Mandy - Mindset & Life Coach',
      page: 'schedule-mandy' as PageId
    }]
  }, {
    id: 'chill',
    title: 'Chill & Vibe Lives',
    emoji: '🎧',
    agents: [{
      name: "Star's Pad Lounge — Chill Days (Wed & Fri)",
      page: 'schedule-star-dj' as PageId
    }, {
      name: 'ZettaMINDset',
      page: 'schedule-zetta' as PageId
    }, {
      name: 'IAM_JDOLLA',
      page: 'schedule-jdolla' as PageId
    }, {
      name: 'J3',
      page: 'schedule-j3' as PageId
    }]
  }];
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-573" data-mp="46nexjwj2xzz">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8
    }} className="text-center mb-12" data-id="element-574" data-mp="1tkiqf0suckns">
        <h2 className="font-['Montserrat'] font-black text-4xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-4" data-id="element-575" data-mp="3izl177zf003a">
          Live Schedules
        </h2>
      </motion.div>
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-576" data-mp="3o10qjc8mksrh">
        {categories.map((cat, i) => <motion.div key={cat.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4 + i * 0.1,
        duration: 0.5
      }} className="backdrop-blur-xl bg-black/10 border border-white/10 rounded-2xl p-6 md:p-8 hover:border-purple-500/30 transition-all flex flex-col" data-id="element-577" data-mp="5mhxlk562p1k" data-mpi={i}>
            <h3 className="font-['Montserrat'] font-black text-2xl text-white mb-6 flex items-center gap-3" data-id="element-578" data-mp="2cdqz3ndi5m35">
              <span className="text-3xl" data-id="element-579" data-mp="1qktjehgx9t3t">{cat.emoji}</span>
              {cat.title}
            </h3>
            {cat.agents.length > 0 ? <div className="flex flex-col gap-3 flex-1" data-id="element-580" data-mp="1vhk1tbg790se">
                {cat.agents.map((agent, idx) => <button key={idx} onClick={() => onNavigate(agent.page)} className="text-left px-4 py-3 rounded-xl bg-white/5 hover:bg-purple-500/20 border border-white/5 hover:border-purple-500/30 transition-all text-white/80 hover:text-white font-['Montserrat'] flex items-center justify-between group" data-id="element-581" data-mp="20c7r3cj11z81" data-mpi={idx}>
                    <span data-id="element-582" data-mp="1hic0abwkdgl1">{agent.name}</span>
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity text-purple-400" data-id="element-583" data-mp="1llqfj0cohsxf">
                      →
                    </span>
                  </button>)}
              </div> : <div className="flex-1 flex items-center justify-center text-white/30 font-['Montserrat'] italic py-8 border border-dashed border-white/10 rounded-xl" data-id="element-584" data-mp="2wysvpuav8gh2">
                Coming soon...
              </div>}
          </motion.div>)}
      </div>
    </div>;
}
function AgentSchedulePage({
  agentName,
  category,
  onBack,
  bannerImage,
  tiktokUrl,
  scheduleData
}: {
  agentName: string;
  category: string;
  onBack: () => void;
  bannerImage?: string;
  tiktokUrl?: string;
  scheduleData?: {
    day: string;
    time: string;
    type: string;
    notes: string;
  }[];
}) {
  const defaultSchedule = [{
    day: 'Monday',
    time: '6:00 PM EST',
    type: 'Battle Live',
    notes: 'Weekly battle stream'
  }, {
    day: 'Tuesday',
    time: '7:00 PM EST',
    type: 'Engagement Live',
    notes: 'Community engagement & chat'
  }, {
    day: 'Wednesday',
    time: '6:00 PM EST',
    type: 'Themed Live',
    notes: 'Special themed content'
  }, {
    day: 'Thursday',
    time: '7:00 PM EST',
    type: 'Collab Live',
    notes: 'Guest collaborations'
  }, {
    day: 'Friday',
    time: '8:00 PM EST',
    type: 'Music / Vibe Live',
    notes: 'Music & chill vibes'
  }, {
    day: 'Saturday',
    time: '3:00 PM EST',
    type: 'Open Live',
    notes: 'Open format — anything goes'
  }, {
    day: 'Sunday',
    time: 'OFF',
    type: 'Rest Day',
    notes: 'Recharge & prep for next week'
  }];
  const schedule = scheduleData || defaultSchedule;
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-585" data-mp="1ok7y4kwfac1l">
      <div className="max-w-4xl mx-auto" data-id="element-586" data-mp="1jlwqmgtlodmj">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-10" data-id="element-587" data-mp="1josltxe2co7a">
          <button onClick={onBack} className="text-white/50 hover:text-white transition-colors font-['Montserrat'] text-sm mb-6 flex items-center gap-2" data-id="element-588" data-mp="3dyd1juxccfxq">
            ← Back to Live Schedules
          </button>
          {bannerImage && <motion.div initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.1,
          duration: 0.5
        }} className="mb-8" data-id="element-589" data-mp="3lifb26aaw5vy">
              {tiktokUrl ? <a href={tiktokUrl} target="_blank" rel="noopener noreferrer" className="block transition-transform hover:scale-[1.01] max-w-sm mx-auto" data-id="element-590" data-mp="32lmi6b51p725">
                  <img src={bannerImage} alt={`${agentName} Banner`} className="w-full h-auto rounded-2xl border border-purple-500/30 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-591" data-mp="mui1ls019uv9" />
                </a> : <div className="max-w-sm mx-auto" data-id="element-592" data-mp="2ixmu7ayvqh6v">
                  <img src={bannerImage} alt={`${agentName} Banner`} className="w-full h-auto rounded-2xl border border-purple-500/30 shadow-[0_0_30px_rgba(168,85,247,0.3)]" data-id="element-593" data-mp="2iwux1vpmwbeh" />
                </div>}
            </motion.div>}
          {!bannerImage && <>
              <h2 className="font-['Montserrat'] font-black text-4xl md:text-5xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-2" data-id="element-594" data-mp="207oec8oubjso">
                {agentName}
              </h2>
              <p className="text-white/50 font-['Montserrat'] text-lg uppercase tracking-wider" data-id="element-595" data-mp="296ieg91dqsaz">
                {category}
              </p>
            </>}
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="backdrop-blur-xl bg-black/10 border border-white/10 rounded-3xl overflow-hidden" data-id="element-596" data-mp="1hh6w4y1mw6lk">
          <div className="p-6 bg-white/5 border-b border-white/10" data-id="element-597" data-mp="xc6e8zst9gyh">
            <h3 className="font-['Montserrat'] font-bold text-2xl text-white flex items-center gap-3" data-id="element-598" data-mp="12e5hm9ojwdvf">
              🗓️ {agentName}'s Weekly Schedule
            </h3>
          </div>
          <div className="divide-y divide-white/5" data-id="element-599" data-mp="1v2b4p332ueyf">
            {schedule.map((item, i) => <motion.div key={item.day} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3 + i * 0.05
          }} className="flex items-center gap-4 px-6 py-4 hover:bg-white/5 transition-colors" data-id="element-600" data-mp="1g5jzueamwb1f" data-mpi={i}>
                <div className="w-28 flex-shrink-0" data-id="element-601" data-mp="pxnuo12k2fhs">
                  <span className="font-['Montserrat'] font-bold text-white text-sm" data-id="element-602" data-mp="l4co811no0qe">
                    {item.day}
                  </span>
                </div>
                <div className="w-28 flex-shrink-0" data-id="element-603" data-mp="b76xwv0v8dr8">
                  <span className={`font-['Montserrat'] text-sm ${item.time === 'OFF' ? 'text-white/30 italic' : 'text-purple-300 font-semibold'}`} data-id="element-604" data-mp="b8rt8mv080q5">
                    {item.time}
                  </span>
                </div>
                <div className="flex-1" data-id="element-605" data-mp="yvq331omcaf3">
                  <span className="font-['Montserrat'] text-white/80 text-sm font-medium" data-id="element-606" data-mp="yym4x13l7j8t">
                    {item.type}
                  </span>
                  <span className="text-white/40 text-xs ml-2" data-id="element-607" data-mp="3jn0etaz3zr8t">
                    — {item.notes}
                  </span>
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </div>;
}
// ── WeeklySchedules ──────────────────────────────────────────────
function WeeklySchedules() {
  const days = [{
    day: 'Monday',
    schedule: '10:00 AM - Team Sync\n2:00 PM - Creator Reviews'
  }, {
    day: 'Tuesday',
    schedule: '11:00 AM - Agency Strategy\n3:00 PM - Open Office Hours'
  }, {
    day: 'Wednesday',
    schedule: '1:00 PM - Mid-week Check-in\n4:00 PM - Content Planning'
  }, {
    day: 'Thursday',
    schedule: '10:30 AM - Live Stream Coaching\n2:30 PM - Growth Workshop'
  }, {
    day: 'Friday',
    schedule: '12:00 PM - Weekly Wrap-up\n3:00 PM - Community Highlights'
  }, {
    day: 'Saturday',
    schedule: 'Weekend Live Events'
  }, {
    day: 'Sunday',
    schedule: 'Rest & Prep for next week'
  }];
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-608" data-mp="xf3yyjg3k20y">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8
    }} className="text-center mb-12" data-id="element-609" data-mp="xhggivhy0adh">
        <h2 className="font-['Montserrat'] font-black text-4xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-4" data-id="element-610" data-mp="zdvhqjjnb9xc">
          Weekly Schedules
        </h2>
        <p className="text-white/50 text-lg font-['Montserrat'] max-w-2xl mx-auto" data-id="element-611" data-mp="18cgzwdl7gmxl">
          Stay up to date with our weekly events, coaching sessions, and team
          syncs.
        </p>
      </motion.div>
      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-612" data-mp="1i20jjlgqskti">
        {days.map((d, i) => <motion.div key={d.day} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2 + i * 0.1,
        duration: 0.5
      }} className="backdrop-blur-xl bg-black/10 border border-white/10 rounded-3xl p-6 md:p-8 hover:border-purple-500/30 transition-all" data-id="element-613" data-mp="1d4sacadknr4e" data-mpi={i}>
            <h3 className="font-['Montserrat'] font-black text-2xl text-white mb-4 flex items-center gap-3" data-id="element-614" data-mp="1woz8n9s4n352">
              <span className="w-2 h-8 rounded-full bg-gradient-to-b from-purple-500 to-cyan-500" data-id="element-615" data-mp="21nu0f182o21b" />
              {d.day}
            </h3>
            <p className="text-white/70 font-['Montserrat'] whitespace-pre-wrap leading-relaxed" data-id="element-616" data-mp="1myed5v7r2eqd">
              {d.schedule}
            </p>
          </motion.div>)}
      </div>
    </div>;
}
// ── Navbar ──────────────────────────────────────────────────────
function Navbar({
  currentPage,
  onNavigate
}: {
  currentPage: PageId;
  onNavigate: (page: PageId) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4" data-id="element-617" data-mp="190pqqiy1azj7">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-md border-b border-white/10" data-id="element-618" data-mp="192apkkrdt4mv" />
      <div className="relative max-w-7xl mx-auto flex items-center justify-between" data-id="element-619" data-mp="1og4vqiwuwpzi">
        {/* Left group: back link (far left) + logo */}
        <div className="flex items-center gap-4" data-id="element-620" data-mp="1fgzw199k6qlr">
          <a href="/" className="hidden md:inline-flex items-center gap-1.5 text-xs font-['Montserrat'] font-semibold text-pink-300 hover:text-pink-200 bg-pink-300/10 hover:bg-pink-300/20 border border-pink-300/25 rounded-full px-3 py-1.5 transition-colors" data-id="element-621" data-mp="1fjm5xfjx9i75">
            <span aria-hidden="true" data-id="element-622" data-mp="10qseqwvafdmn">←</span>
            BACK TO TRIPPINWITHMANDY.COM
          </a>
          {/* Logo */}
          <button onClick={() => onNavigate('home')} className="flex items-center gap-2 group" data-id="element-623" data-mp="rsgqh1hw8rxd">
            <span className="font-['Montserrat'] font-black text-2xl tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white to-purple-300 group-hover:to-blue-300 transition-all duration-300" data-id="element-624" data-mp="ms2fbakzx1v4">
              TWM
            </span>
          </button>
        </div>
        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8" data-id="element-625" data-mp="cnvskdnuy818">
          {NAV_ITEMS.map(item => <button key={item.label} onClick={() => onNavigate(item.page)} className={`relative font-['Inter'] text-sm font-medium transition-colors py-2 group ${currentPage === item.page ? 'text-white' : 'text-white/80 hover:text-white'}`} data-id="element-626" data-mp="hpl743aah3u2">
              {item.label}
              <span className={`absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-purple-500 to-blue-500 transition-transform duration-300 origin-left shadow-[0_0_8px_rgba(168,85,247,0.6)] ${currentPage === item.page ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} data-id="element-627" data-mp="3q4d2tlxsyeg7" />
            </button>)}
          <div className="h-6 w-[1px] bg-white/20 mx-2" data-id="element-628" data-mp="37csjlajk7ifg" />
          <div className="flex items-center gap-4" data-id="element-629" data-mp="37bhixjleuqsk">
            <button onClick={() => onNavigate('agent-login')} className="text-xs font-['Montserrat'] font-semibold text-white/50 hover:text-purple-300 transition-colors" data-id="element-630" data-mp="2fo7tlqvcvr0b">
              Agent Login
            </button>
            <button onClick={() => onNavigate('creator-login')} className="text-xs font-['Montserrat'] font-semibold text-white/50 hover:text-cyan-300 transition-colors" data-id="element-631" data-mp="2ao3bxxipoq8i">
              Creator Login
            </button>
          </div>
        </div>
        {/* Mobile Toggle */}
        <button onClick={() => setIsOpen(!isOpen)} className="md:hidden relative z-50 p-2 text-white hover:text-white transition-colors" data-id="element-632" data-mp="1mz0w8v7n17gn">
          {isOpen ? <XIcon size={24} data-id="element-633" data-mp="1dxsxyd54g6mk" /> : <MenuIcon size={24} data-id="element-634" data-mp="1dxav2xnyeu5o" />}
        </button>
      </div>
      {/* Mobile Menu */}
      <AnimatePresence data-id="element-635" data-mp="1hz664tvoslwn">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.2
      }} className="absolute top-full left-0 right-0 bg-black/80 backdrop-blur-xl border-b border-white/10 md:hidden" data-id="element-636" data-mp="1hywe70r304ob">
            <div className="flex flex-col px-6 py-4 gap-4" data-id="element-637" data-mp="1i8y6am09hywz">
              {NAV_ITEMS.map(item => <button key={item.label} onClick={() => {
            onNavigate(item.page);
            setIsOpen(false);
          }} className={`text-left font-['Inter'] text-lg font-medium hover:pl-2 transition-all duration-300 border-l-2 ${currentPage === item.page ? 'text-white border-purple-500 pl-2' : 'text-white hover:text-white'}`} data-id="element-638" data-mp="1d9u0848mwhav">
                  {item.label}
                </button>)}
              <div className="w-full h-[1px] bg-white/10 my-2" data-id="element-639" data-mp="tp9oj71h22jn" />
              <a href="/" className="inline-flex items-center gap-1.5 text-left font-['Montserrat'] text-sm font-semibold text-pink-300 hover:text-pink-200 transition-colors" data-id="element-640" data-mp="xp225y5q2yvv">
                <span aria-hidden="true" data-id="element-641" data-mp="17gesglbvhgzs">←</span>
                BACK TO TRIPPINWITHMANDY.COM
              </a>
              <button onClick={() => {
            onNavigate('agent-login');
            setIsOpen(false);
          }} className="text-left font-['Montserrat'] text-sm font-semibold text-white/50 hover:text-purple-300 transition-colors" data-id="element-642" data-mp="1cdojg5ewu0pq">
                Agent Login
              </button>
              <button onClick={() => {
            onNavigate('creator-login');
            setIsOpen(false);
          }} className="text-left font-['Montserrat'] text-sm font-semibold text-white/50 hover:text-cyan-300 transition-colors" data-id="element-643" data-mp="3n6rc9qbjwpet">
                Creator Login
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}
// ── LoginPage ──────────────────────────────────────────────────
function LoginPage({
  title,
  type,
  onLogin
}: {
  title: string;
  type: 'agent' | 'creator';
  onLogin: (displayName: string, role: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError('');
    try {
      let cred;
      if (type === 'agent') {
        cred = await signInWithEmailAndPassword(auth, email, password);
      } else {
        cred = await signInWithEmailAndPassword(creatorAuth, email, password);
      }
      let displayName = email.split('@')[0];
      let userRole = 'creator';
      try {
        const uid = cred.user.uid;
        const userDocSnap = await getDoc(doc(db, 'users', uid));
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          displayName = userData?.Name || userData?.name || displayName;
          userRole = userData?.role || 'creator';
        }
      } catch (e) {
        // Fallback
      }
      if (type === 'agent' && userRole === 'creator') {
        await signOut(auth);
        setError('Creator accounts cannot access the Agent portal. Please use the Creator Login instead.');
        return;
      }
      onLogin(displayName, userRole);
    } catch (err: any) {
      setError(err.message || 'Failed to login. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };
  return <div className="relative z-10 min-h-screen flex items-center justify-center px-4 pt-20" data-id="element-644" data-mp="34fgd9dyyjq5i">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.4
    }} className="max-w-md w-full backdrop-blur-xl bg-black/40 border border-white/10 rounded-3xl p-8 shadow-[0_0_40px_rgba(168,85,247,0.15)]" data-id="element-645" data-mp="2wv5udkp33d0u">
        <div className="text-center mb-8" data-id="element-646" data-mp="31so43daxdw9w">
          <h2 className="font-['Montserrat'] font-black text-3xl text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-cyan-300 mb-2" data-id="element-647" data-mp="2e5zh4d727dmf">
            {title}
          </h2>
          <p className="text-white/50 text-sm font-['Montserrat']" data-id="element-648" data-mp="2nv99lu5teyb2">
            Enter your credentials to access the portal
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-649" data-mp="2j2px70iv697u">
          {error && <div className="p-3 rounded-xl bg-red-500/20 border border-red-500/50 text-red-200 text-sm text-center" data-id="element-650" data-mp="1vcvjkgqq0gmj">
              {error}
            </div>}
          <div data-id="element-651" data-mp="2g4akkvcpd9h0">
            <label className="block text-white/70 text-sm font-medium mb-2 font-['Montserrat']" data-id="element-652" data-mp="2g2fvr5yb8x7n">
              Email
            </label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all font-['Inter']" placeholder="Enter your email" required data-id="element-653" data-mp="2k2ztobqpk4uu" />
          </div>
          <div data-id="element-654" data-mp="2649w6uzz835y">
            <label className="block text-white/70 text-sm font-medium mb-2 font-['Montserrat']" data-id="element-655" data-mp="263qffhm2bkti">
              Password
            </label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all font-['Inter']" placeholder="••••••••" required data-id="element-656" data-mp="3hkgrhhd636v1" />
          </div>
          <button type="submit" disabled={loading} className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-base font-['Montserrat'] shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-id="element-657" data-mp="114tu35aa7awu">
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </motion.div>
    </div>;
}
// ── Portal Interfaces & Components ────────────────────────────────
interface PostLike {
  email: string;
  name: string;
}
interface Post {
  id: string;
  content: string;
  imageUrl?: string;
  authorName: string;
  authorInitials: string;
  createdAt: Timestamp | null;
  sharedToCreators?: boolean;
  postedBy?: 'agent' | 'creator';
  crossPosted?: boolean;
  fontColor?: string;
  fontSize?: string;
  pinned?: boolean;
  likes?: PostLike[];
  comments: {
    content: string;
    authorName: string;
    authorInitials: string;
    createdAt: Timestamp | null;
  }[];
}
interface MeetingNote {
  notes: string;
  title: string;
  updatedAt: Timestamp | null;
  updatedBy: string;
}
function PortalPage({
  user,
  displayName,
  userRole,
  title,
  subtitle,
  collectionName,
  notesCollectionName,
  onSignOut,
  onBack
}: {
  user: User | null;
  displayName: string;
  userRole: string;
  title: string;
  subtitle: string;
  collectionName: string;
  notesCollectionName: string;
  onSignOut: () => void;
  onBack: () => void;
}) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [meetingNotesText, setMeetingNotesText] = useState('');
  const [meetingNotesDocId, setMeetingNotesDocId] = useState('');
  const [lastUpdatedBy, setLastUpdatedBy] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Timestamp | null>(null);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  const [commentInputs, setCommentInputs] = useState<{
    [postId: string]: string;
  }>({});
  const [isPosting, setIsPosting] = useState(false);
  const [shareToCreators, setShareToCreators] = useState(false);
  const [fontColor, setFontColor] = useState('#ffffff');
  const [fontSize, setFontSize] = useState('base');
  useEffect(() => {
    const q = query(collection(db, collectionName), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, snapshot => {
      const postsData: Post[] = [];
      snapshot.forEach(doc => {
        postsData.push({
          id: doc.id,
          ...doc.data()
        } as Post);
      });
      postsData.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return 0;
      });
      setPosts(postsData);
    });
    const notesDocRef = doc(db, 'site_content', notesCollectionName);
    const notesUnsubscribe = onSnapshot(notesDocRef, docSnap => {
      if (docSnap.exists()) {
        const data = docSnap.data() as MeetingNote;
        setMeetingNotesDocId(docSnap.id);
        setMeetingNotesText(data.notes || '');
        setLastUpdatedBy(data.updatedBy || '');
        setLastUpdatedAt(data.updatedAt);
      } else {
        setMeetingNotesDocId('');
        setMeetingNotesText('');
        setLastUpdatedBy('');
        setLastUpdatedAt(null);
      }
    });
    return () => {
      unsubscribe();
      notesUnsubscribe();
    };
  }, [collectionName, notesCollectionName]);
  const handlePost = async () => {
    if (!newPostContent.trim() || !user) return;
    setIsPosting(true);
    try {
      const name = displayName || user.email?.split('@')[0] || 'Agent';
      const initials = name.substring(0, 2).toUpperCase();
      await addDoc(collection(db, collectionName), {
        content: newPostContent,
        authorName: name,
        authorInitials: initials,
        createdAt: serverTimestamp(),
        comments: [],
        postedBy: 'agent',
        crossPosted: shareToCreators,
        fontColor,
        fontSize
      });
      setNewPostContent('');
      setShareToCreators(false);
      setFontColor('#ffffff');
      setFontSize('base');
    } catch (error: any) {
      console.error('Error adding post:', error);
      alert('Failed to post: ' + (error?.message || 'Unknown error.'));
    } finally {
      setIsPosting(false);
    }
  };
  const handleSaveMeetingNotes = async () => {
    if (!user) return;
    setIsSavingNotes(true);
    try {
      const name = displayName || user.email?.split('@')[0] || 'Agent';
      const notesDocRef = doc(db, 'site_content', notesCollectionName);
      await setDoc(notesDocRef, {
        notes: meetingNotesText,
        title: 'Weekly Meeting Notes',
        updatedAt: serverTimestamp(),
        updatedBy: name
      }, {
        merge: true
      });
      setMeetingNotesDocId(notesCollectionName);
    } catch (error) {
      console.error('Error saving meeting notes:', error);
      alert('Failed to save notes.');
    } finally {
      setIsSavingNotes(false);
    }
  };
  const handlePinPost = async (postId: string, currentPinned: boolean) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, collectionName, postId), {
        pinned: !currentPinned
      });
    } catch (error) {
      console.error('Error pinning post:', error);
    }
  };
  const handleLike = async (post: Post) => {
    if (!user) return;
    const email = user.email || '';
    const name = displayName || email.split('@')[0] || 'Agent';
    const alreadyLiked = post.likes?.some(l => l.email === email);
    const postRef = doc(db, collectionName, post.id);
    try {
      if (alreadyLiked) {
        const updatedLikes = (post.likes || []).filter(l => l.email !== email);
        await updateDoc(postRef, {
          likes: updatedLikes
        });
      } else {
        await updateDoc(postRef, {
          likes: arrayUnion({
            email,
            name
          })
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };
  const handleDeletePost = async (postId: string) => {
    if (!user) return;
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      await deleteDoc(doc(db, collectionName, postId));
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };
  const handleComment = async (postId: string) => {
    const content = commentInputs[postId];
    if (!content?.trim() || !user) return;
    try {
      const name = displayName || user.email?.split('@')[0] || 'Agent';
      const initials = name.substring(0, 2).toUpperCase();
      const postRef = doc(db, collectionName, postId);
      await updateDoc(postRef, {
        comments: arrayUnion({
          content,
          authorName: name,
          authorInitials: initials,
          createdAt: Timestamp.now()
        })
      });
      setCommentInputs(prev => ({
        ...prev,
        [postId]: ''
      }));
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };
  const handleDeleteComment = async (postId: string, commentToDelete: {
    content: string;
    authorName: string;
    authorInitials: string;
    createdAt: Timestamp | null;
  }) => {
    if (!user) return;
    const currentName = displayName || user.email?.split('@')[0] || 'Agent';
    if (commentToDelete.authorName !== currentName) return;
    try {
      const postRef = doc(db, collectionName, postId);
      const postSnap = await getDoc(postRef);
      if (postSnap.exists()) {
        const data = postSnap.data();
        const updatedComments = (data.comments || []).filter((c: any) => !(c.content === commentToDelete.content && c.authorName === commentToDelete.authorName && c.createdAt?.seconds === commentToDelete.createdAt?.seconds));
        await updateDoc(postRef, {
          comments: updatedComments
        });
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-658" data-mp="16i4i88jnteau">
      <div className="max-w-6xl mx-auto" data-id="element-659" data-mp="1bh8sl2ibli2r">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-10 flex items-start justify-between" data-id="element-660" data-mp="1bg61ath35dmb">
          <div data-id="element-661" data-mp="3i7ykqdf2mz1x">
            <div className="flex items-center gap-4 mb-2" data-id="element-662" data-mp="3i7fdbqn1rt25">
              <button onClick={onBack} className="text-white/50 hover:text-white transition-colors font-['Montserrat'] text-sm" data-id="element-663" data-mp="3ia1khp6106ju">
                ← Back
              </button>
              <h2 className="font-['Montserrat'] font-black text-3xl md:text-4xl text-white" data-id="element-664" data-mp="1nrqbdrs5p24z">
                {title}
              </h2>
            </div>
            <p className="text-white/50 font-['Montserrat']" data-id="element-665" data-mp="1izfd0td2doej">{subtitle}</p>
          </div>
          {user && <button onClick={onSignOut} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl backdrop-blur-xl bg-black/40 border border-white/10 text-white text-sm font-['Montserrat'] hover:border-red-500/30 hover:text-red-300 transition-all" data-id="element-666" data-mp="1e1ncxc29mvz9">
              <LogOutIcon size={16} data-id="element-667" data-mp="102e818jifoal" />
              Sign Out
            </button>}
        </motion.div>
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-668" data-mp="v55p35qjfg8a">
          {/* Left Column: Chat Feed */}
          <div className="flex-1 min-w-0" data-id="element-669" data-mp="2ue2ml9ltxoug">
            {user && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="backdrop-blur-md bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6 mb-8" data-id="element-670" data-mp="2pidl30q91xvi">
                <textarea value={newPostContent} onChange={e => setNewPostContent(e.target.value)} style={{
              color: fontColor
            }} className={`w-full bg-black/20 border border-white/5 rounded-xl p-4 placeholder-white/40 focus:outline-none focus:border-purple-500/50 resize-none font-['Inter'] ${fontSize === 'sm' ? 'text-sm' : fontSize === 'lg' ? 'text-lg' : fontSize === 'xl' ? 'text-xl' : fontSize === '2xl' ? 'text-2xl' : 'text-base'}`} rows={3} placeholder="Share an update with the team..." data-id="element-671" data-mp="3brlu715ylg25" />
                <div className="flex flex-wrap items-center gap-3 mt-4" data-id="element-672" data-mp="tdusk0yu0rgs">
                  <div className="flex items-center gap-2" data-id="element-673" data-mp="pbrh3cc5xmvi">
                    <span className="text-white/40 text-xs font-['Montserrat']" data-id="element-674" data-mp="p951qqj1632q">
                      Color:
                    </span>
                    <div className="flex gap-1.5" data-id="element-675" data-mp="ybe7l33ct89s">
                      {[{
                    color: '#ffffff',
                    label: 'White'
                  }, {
                    color: '#c084fc',
                    label: 'Purple'
                  }, {
                    color: '#22d3ee',
                    label: 'Cyan'
                  }, {
                    color: '#f472b6',
                    label: 'Pink'
                  }, {
                    color: '#facc15',
                    label: 'Yellow'
                  }, {
                    color: '#4ade80',
                    label: 'Green'
                  }, {
                    color: '#f87171',
                    label: 'Red'
                  }].map(c => <button key={c.color} type="button" onClick={() => setFontColor(c.color)} title={c.label} className={`w-6 h-6 rounded-full border-2 transition-all ${fontColor === c.color ? 'border-white scale-110' : 'border-white/20 hover:border-white/50'}`} style={{
                    backgroundColor: c.color
                  }} data-id="element-676" data-mp="1fli6qaobeop1" />)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-677" data-mp="bkg4avrwjh4o">
                    <span className="text-white/40 text-xs font-['Montserrat']" data-id="element-678" data-mp="bhtqieef45e0">
                      Size:
                    </span>
                    <div className="flex gap-1" data-id="element-679" data-mp="kk2vyputx8kj">
                      {[{
                    value: 'sm',
                    label: 'S'
                  }, {
                    value: 'base',
                    label: 'M'
                  }, {
                    value: 'lg',
                    label: 'L'
                  }, {
                    value: 'xl',
                    label: 'XL'
                  }, {
                    value: '2xl',
                    label: '2XL'
                  }].map(s => <button key={s.value} type="button" onClick={() => setFontSize(s.value)} className={`px-2 py-0.5 rounded text-xs font-['Montserrat'] font-bold transition-all ${fontSize === s.value ? 'bg-purple-500/30 border border-purple-500/50 text-purple-300' : 'bg-white/5 border border-white/10 text-white/40 hover:text-white/70'}`} data-id="element-680" data-mp="3a3d9yy8v3ue0">
                          {s.label}
                        </button>)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3" data-id="element-681" data-mp="3cffh9svbrczc">
                  <button type="button" onClick={() => setShareToCreators(!shareToCreators)} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all font-['Montserrat'] text-sm ${shareToCreators ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300' : 'bg-white/5 border-white/10 text-white/40 hover:text-white/70'}`} data-id="element-682" data-mp="37h469etamwfj">
                    <span className="text-base" data-id="element-683" data-mp="22ih4a781xvfl">
                      {shareToCreators ? '✅' : '⬜'}
                    </span>
                    Cross-post to Creators
                  </button>
                  <button onClick={handlePost} disabled={isPosting || !newPostContent.trim()} className="rounded-lg bg-purple-600/80 hover:bg-purple-500 text-white font-semibold text-sm px-4 py-2" data-id="element-684" data-mp="1sji1h59ywu0i">
                    {isPosting ? 'Posting...' : 'Post Update'}
                  </button>
                </div>
              </motion.div>}
            {!user && <div className="mb-8 p-4 rounded-xl bg-purple-500/20 border border-purple-500/30 text-purple-200 text-center font-['Montserrat']" data-id="element-685" data-mp="19rgybqq5uhla">
                You are viewing the portal in read-only mode. Please login as an
                Agent to post and comment.
              </div>}
            {/* Mobile Meeting Notes */}
            <div className="lg:hidden mb-8 backdrop-blur-md bg-black/40 border border-white/10 rounded-2xl overflow-hidden" data-id="element-686" data-mp="c8ibsc3143ir">
              <button onClick={() => setIsNotesExpanded(!isNotesExpanded)} className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors" data-id="element-687" data-mp="q8jcnrjanay8">
                <h3 className="font-['Montserrat'] font-bold text-xl text-white flex items-center gap-2" data-id="element-688" data-mp="103uuwjtwv0aq">
                  📝 Weekly Meeting Notes
                </h3>
                <span className="text-white/50" data-id="element-689" data-mp="19ufr60m43sqh">
                  {isNotesExpanded ? '▲' : '▼'}
                </span>
              </button>
              <AnimatePresence data-id="element-690" data-mp="14z0gtinbceyt">
                {isNotesExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="border-t border-white/10" data-id="element-691" data-mp="1iwozqkxtlc18">
                    <div className="p-4 space-y-4" data-id="element-692" data-mp="3m3weqlud22n9">
                      {user ? <div className="flex flex-col gap-3" data-id="element-693" data-mp="2pfgnmlblrl81">
                          <textarea value={meetingNotesText} onChange={e => setMeetingNotesText(e.target.value)} placeholder="Type your meeting notes here..." className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-purple-500 resize-y min-h-[120px] font-['Inter']" data-id="element-694" data-mp="2ph1ikc1tx66f" />
                          <div className="flex items-center justify-between" data-id="element-695" data-mp="sr4cqvd44jwc">
                            <div className="text-white/40 text-xs font-['Montserrat']" data-id="element-696" data-mp="nskrkx6cfc51">
                              {lastUpdatedBy && <>
                                  Last updated by {lastUpdatedBy}
                                  {lastUpdatedAt && ` on ${safeToDate(lastUpdatedAt)?.toLocaleDateString() ?? ''}`}
                                </>}
                            </div>
                            <button onClick={handleSaveMeetingNotes} disabled={isSavingNotes} className="px-4 py-2 bg-purple-600/80 hover:bg-purple-500 rounded-lg text-white text-sm font-semibold disabled:opacity-50 transition-colors" data-id="element-697" data-mp="2yq232i0ktv">
                              {isSavingNotes ? 'Saving...' : 'Save Notes'}
                            </button>
                          </div>
                        </div> : <div className="bg-white/5 rounded-xl p-4 border border-white/5" data-id="element-698" data-mp="3oqog5cpk960e">
                          <p className="text-white/80 text-sm whitespace-pre-wrap font-['Inter']" data-id="element-699" data-mp="3aztcqfxtzh47">
                            {meetingNotesText || <span className="italic text-white/40" data-id="element-700" data-mp="6j8qd72gc5j8">
                                No meeting notes available.
                              </span>}
                          </p>
                          {lastUpdatedBy && <div className="mt-3 pt-3 border-t border-white/10 text-white/40 text-xs font-['Montserrat']" data-id="element-701" data-mp="3tqsyl8a14at9">
                              Last updated by {lastUpdatedBy}
                              {lastUpdatedAt && ` on ${safeToDate(lastUpdatedAt)?.toLocaleDateString() ?? ''}`}
                            </div>}
                        </div>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
            {/* Feed */}
            <div className="space-y-6" data-id="element-702" data-mp="2ongtl1hz68yz">
              {posts.length === 0 ? <div className="text-center py-10 text-white/40 font-['Montserrat']" data-id="element-703" data-mp="2jqzz7pk6mydg">
                  No posts yet. Be the first to share an update!
                </div> : posts.map((post, idx) => <motion.div key={post.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2 + idx * 0.1
            }} className={`backdrop-blur-md rounded-2xl p-5 md:p-6 ${post.pinned ? 'bg-black/40 border border-purple-500/50 shadow-[0_0_15px_rgba(168,85,247,0.2)]' : post.crossPosted ? 'bg-cyan-900/20 border border-cyan-500/20 shadow-[0_0_15px_rgba(34,211,238,0.1)]' : 'bg-black/40 border border-white/5'}`} data-id="element-704" data-mp="25qp4gbfr4zn0" data-mpi={idx}>
                    {post.pinned && <div className="flex items-center gap-2 text-purple-400 text-xs font-bold font-['Montserrat'] mb-3 uppercase tracking-wider" data-id="element-705" data-mp="11n65zzihk0lf">
                        📌 Pinned Post
                      </div>}
                    <div className="flex items-center gap-3 mb-4" data-id="element-706" data-mp="1fgxed4khdefk">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-sm" data-id="element-707" data-mp="1fiqncqikgaqq">
                        {post.authorInitials}
                      </div>
                      <div className="flex-1" data-id="element-708" data-mp="1tfngiym5q4as">
                        <div className="flex items-center gap-2" data-id="element-709" data-mp="1tecbywkw0sim">
                          <h4 className="text-white font-semibold font-['Montserrat']" data-id="element-710" data-mp="3dz914kdmkq0f">
                            {post.authorName}
                          </h4>
                          {post.crossPosted && <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-[10px] font-semibold font-['Montserrat']" data-id="element-711" data-mp="34yb3qkxozgqt">
                              Shared to Creators
                            </span>}
                        </div>
                        <p className="text-white/40 text-xs" data-id="element-712" data-mp="3spgl54ja6h38">
                          {post.createdAt ? safeToDate(post.createdAt)?.toLocaleString() ?? 'Just now' : 'Just now'}
                        </p>
                      </div>
                      {user && <div className="flex items-center gap-2" data-id="element-713" data-mp="ffsofw8jz8ru">
                          <button onClick={() => handlePinPost(post.id, !!post.pinned)} className={`transition-colors text-sm font-['Montserrat'] p-1 ${post.pinned ? 'text-purple-400 hover:text-purple-300' : 'text-white/20 hover:text-purple-400'}`} title={post.pinned ? 'Unpin post' : 'Pin post'} data-id="element-714" data-mp="x42degc6yxjd">
                            📌
                          </button>
                          <button onClick={() => handleDeletePost(post.id)} className="text-white/20 hover:text-red-400 transition-colors text-sm font-['Montserrat'] p-1" title="Delete post" data-id="element-715" data-mp="1awibd2q8k5df">
                            🗑️
                          </button>
                        </div>}
                    </div>
                    {post.content && <p className={`leading-relaxed mb-4 whitespace-pre-wrap ${post.fontSize === 'sm' ? 'text-sm' : post.fontSize === 'lg' ? 'text-lg' : post.fontSize === 'xl' ? 'text-xl' : post.fontSize === '2xl' ? 'text-2xl' : 'text-sm md:text-base'}`} style={{
                color: post.fontColor || 'rgba(255,255,255,0.8)'
              }} data-id="element-716" data-mp="3chuth6xbafht">
                        {post.content}
                      </p>}
                    <div className="mb-4 flex items-center gap-3" data-id="element-717" data-mp="48cotfwrmvp1">
                      <button onClick={() => user && handleLike(post)} className={`flex items-center gap-1.5 text-sm font-['Montserrat'] transition-all ${!user ? 'text-white/30 cursor-default' : post.likes?.some(l => l.email === user.email) ? 'text-pink-400' : 'text-white/40 hover:text-pink-400'}`} data-id="element-718" data-mp="8zujjm87401k">
                        <span className="text-lg" data-id="element-719" data-mp="2j3a0a0gyvmun">
                          {user && post.likes?.some(l => l.email === user.email) ? '❤️' : '🤍'}
                        </span>
                        <span data-id="element-720" data-mp="2suo3ojc1h6t2">{post.likes?.length || 0}</span>
                      </button>
                      {(post.likes?.length || 0) > 0 && <p className="text-white/30 text-xs font-['Montserrat']" data-id="element-721" data-mp="31t8i38akgubh">
                          Liked by {post.likes?.map(l => l.name).join(', ')}
                        </p>}
                    </div>
                    <div className="border-t border-white/10 pt-4 space-y-4" data-id="element-722" data-mp="3bqmixzk0baio">
                      {post.comments?.map((comment, cIdx) => <div key={cIdx} className="flex gap-3" data-id="element-723" data-mp="1hdws5qoxqhfi">
                          <div className="w-8 h-8 rounded-full bg-pink-500/50 flex-shrink-0 flex items-center justify-center text-white text-xs font-bold" data-id="element-724" data-mp="1heg7czn7arpu">
                            {comment.authorInitials}
                          </div>
                          <div className="bg-white/5 rounded-2xl rounded-tl-none p-3 flex-1" data-id="element-725" data-mp="3t10at2hedc7b">
                            <div className="flex justify-between items-baseline mb-1" data-id="element-726" data-mp="3ecdjs3ieqw09">
                              <h5 className="text-white/90 text-sm font-semibold" data-id="element-727" data-mp="3eaj7tep46e8b">
                                {comment.authorName}
                              </h5>
                              <div className="flex items-center gap-2" data-id="element-728" data-mp="35a4djpu69oso">
                                <span className="text-white/30 text-[10px]" data-id="element-729" data-mp="3abtn111qxuen">
                                  {comment.createdAt ? safeToDate(comment.createdAt)?.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          }) ?? '' : ''}
                                </span>
                                {user && comment.authorName === (displayName || user.email?.split('@')[0] || '') && <button onClick={() => handleDeleteComment(post.id, comment)} className="text-white/20 hover:text-red-400 transition-colors text-xs" title="Delete comment" data-id="element-730" data-mp="2go35c1eugtkc">
                                      ✕
                                    </button>}
                              </div>
                            </div>
                            <p className="text-white/70 text-sm whitespace-pre-wrap" data-id="element-731" data-mp="1o0pgkqlav4bb">
                              {comment.content}
                            </p>
                          </div>
                        </div>)}
                      {user && <div className="flex gap-3 items-center mt-4" data-id="element-732" data-mp="1nvhq0crqqdnj">
                          <div className="w-8 h-8 rounded-full bg-white/10 flex-shrink-0 flex items-center justify-center text-white/50 text-xs font-bold" data-id="element-733" data-mp="1stjbt5gjwr2p">
                            {displayName ? displayName.substring(0, 2).toUpperCase() : user.email?.substring(0, 2).toUpperCase()}
                          </div>
                          <div className="flex-1 flex gap-2" data-id="element-734" data-mp="19u5cs93ysq5c">
                            <input type="text" value={commentInputs[post.id] || ''} onChange={e => setCommentInputs(prev => ({
                      ...prev,
                      [post.id]: e.target.value
                    }))} onKeyDown={e => e.key === 'Enter' && handleComment(post.id)} placeholder="Add a comment..." className="flex-1 bg-transparent border-none text-white text-sm focus:outline-none placeholder-white/30" data-id="element-735" data-mp="19vq9a49uaj5u" />
                            <button onClick={() => handleComment(post.id)} disabled={!commentInputs[post.id]?.trim()} className="text-purple-400 hover:text-purple-300" data-id="element-736" data-mp="3hmyctfxeluzi">
                              Post
                            </button>
                          </div>
                        </div>}
                    </div>
                  </motion.div>)}
            </div>
          </div>
          {/* Right Column: Meeting Notes Sidebar */}
          <div className="hidden lg:block w-80 flex-shrink-0" data-id="element-737" data-mp="1j91zl1qtiuyj">
            <div className="sticky top-28 backdrop-blur-md bg-black/40 border border-white/10 rounded-2xl overflow-hidden" data-id="element-738" data-mp="15d7yv6077bwi">
              <div className="p-4 bg-white/5 border-b border-white/10" data-id="element-739" data-mp="19f1vn379vvre">
                <h3 className="font-['Montserrat'] font-bold text-lg text-white flex items-center gap-2" data-id="element-740" data-mp="1x3copk6q8r54">
                  📝 Weekly Meeting Notes
                </h3>
              </div>
              <div className="p-4 space-y-4" data-id="element-741" data-mp="1o9pwg41qa60o">
                {user ? <div className="flex flex-col gap-3" data-id="element-742" data-mp="1sbl03f74svhm">
                    <textarea value={meetingNotesText} onChange={e => setMeetingNotesText(e.target.value)} placeholder="Type your meeting notes here..." className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-purple-500 resize-y min-h-[200px] font-['Inter']" data-id="element-743" data-mp="1sa019ddsaqdy" />
                    <button onClick={handleSaveMeetingNotes} disabled={isSavingNotes} className="w-full px-4 py-2 bg-purple-600/80 hover:bg-purple-500 rounded-lg text-white text-sm font-semibold disabled:opacity-50 transition-colors" data-id="element-744" data-mp="1mczp3fy1s162">
                      {isSavingNotes ? 'Saving...' : 'Save Notes'}
                    </button>
                    {lastUpdatedBy && <div className="text-white/40 text-xs font-['Montserrat'] text-center mt-2" data-id="element-745" data-mp="203cupbngv3s8">
                        Last updated by {lastUpdatedBy}
                        <br data-id="element-746" data-mp="tou2sorci444" />
                        {lastUpdatedAt && (safeToDate(lastUpdatedAt)?.toLocaleString() ?? '')}
                      </div>}
                  </div> : <div className="bg-white/5 rounded-xl p-4 border border-white/5" data-id="element-747" data-mp="35am8ba4h2es5">
                    <p className="text-white/80 text-sm whitespace-pre-wrap font-['Inter']" data-id="element-748" data-mp="3589pyvusy2ek">
                      {meetingNotesText || <span className="italic text-white/40" data-id="element-749" data-mp="3f0p75qy9ymye">
                          No meeting notes available.
                        </span>}
                    </p>
                    {lastUpdatedBy && <div className="mt-4 pt-3 border-t border-white/10 text-white/40 text-xs font-['Montserrat']" data-id="element-750" data-mp="3nzao5ho3ltx2">
                        Last updated by {lastUpdatedBy}
                        <br data-id="element-751" data-mp="1suk86tgiez8" />
                        {lastUpdatedAt && (safeToDate(lastUpdatedAt)?.toLocaleString() ?? '')}
                      </div>}
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// ── CreatorPortalPage ───────────────────────────────────────────
function CreatorPortalPage({
  user,
  displayName,
  userRole,
  title,
  subtitle,
  collectionName,
  notesCollectionName,
  onSignOut,
  onBack
}: {
  user: User | null;
  displayName: string;
  userRole: string;
  title: string;
  subtitle: string;
  collectionName: string;
  notesCollectionName: string;
  onSignOut: () => void;
  onBack: () => void;
}) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [meetingNotesText, setMeetingNotesText] = useState('');
  const [meetingNotesDocId, setMeetingNotesDocId] = useState('');
  const [lastUpdatedBy, setLastUpdatedBy] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Timestamp | null>(null);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  const [commentInputs, setCommentInputs] = useState<{
    [postId: string]: string;
  }>({});
  const [isPosting, setIsPosting] = useState(false);
  const [fontColor, setFontColor] = useState('#ffffff');
  const [fontSize, setFontSize] = useState('base');
  const [newMeetingNote, setNewMeetingNote] = useState('');
  const canPost = userRole === 'agent' || userRole === 'admin';
  useEffect(() => {
    const q = query(collection(db, collectionName), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, snapshot => {
      const postsData: Post[] = [];
      snapshot.forEach(doc => {
        postsData.push({
          id: doc.id,
          ...doc.data()
        } as Post);
      });
      const filtered = userRole === 'agent' || userRole === 'admin' ? postsData : postsData.filter(p => p.postedBy === 'creator' || p.crossPosted === true);
      filtered.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return 0;
      });
      setPosts(filtered);
    });
    const notesDocRef = doc(db, 'site_content', notesCollectionName);
    const notesUnsubscribe = onSnapshot(notesDocRef, docSnap => {
      if (docSnap.exists()) {
        const data = docSnap.data() as MeetingNote;
        setMeetingNotesDocId(docSnap.id);
        setMeetingNotesText(data.notes || '');
        setLastUpdatedBy(data.updatedBy || '');
        setLastUpdatedAt(data.updatedAt);
      } else {
        setMeetingNotesDocId('');
        setMeetingNotesText('');
        setLastUpdatedBy('');
        setLastUpdatedAt(null);
      }
    });
    return () => {
      unsubscribe();
      notesUnsubscribe();
    };
  }, [collectionName, notesCollectionName, userRole]);
  const handlePost = async () => {
    if (!newPostContent.trim() || !user || !canPost) return;
    setIsPosting(true);
    try {
      const name = displayName || user.email?.split('@')[0] || 'Creator';
      const initials = name.substring(0, 2).toUpperCase();
      await addDoc(collection(db, collectionName), {
        content: newPostContent,
        authorName: name,
        authorInitials: initials,
        createdAt: serverTimestamp(),
        comments: [],
        postedBy: 'creator',
        fontColor,
        fontSize
      });
      setNewPostContent('');
      setFontColor('#ffffff');
      setFontSize('base');
    } catch (error: any) {
      console.error('Error adding post:', error);
      alert('Failed to post: ' + (error?.message || 'Unknown error.'));
    } finally {
      setIsPosting(false);
    }
  };
  const handleSaveMeetingNotes = async () => {
    if (!user || !canPost) return;
    setIsSavingNotes(true);
    try {
      const name = displayName || user.email?.split('@')[0] || 'Agent';
      const notesDocRef = doc(db, 'site_content', notesCollectionName);
      await setDoc(notesDocRef, {
        notes: meetingNotesText,
        title: 'Weekly Meeting Notes',
        updatedAt: serverTimestamp(),
        updatedBy: name
      }, {
        merge: true
      });
      setMeetingNotesDocId(notesCollectionName);
    } catch (error) {
      console.error('Error saving meeting notes:', error);
      alert('Failed to save notes.');
    } finally {
      setIsSavingNotes(false);
    }
  };
  const handleLike = async (post: Post) => {
    if (!user) return;
    const email = user.email || '';
    const name = displayName || email.split('@')[0] || 'Creator';
    const alreadyLiked = post.likes?.some(l => l.email === email);
    const postRef = doc(db, collectionName, post.id);
    try {
      if (alreadyLiked) {
        const updatedLikes = (post.likes || []).filter(l => l.email !== email);
        await updateDoc(postRef, {
          likes: updatedLikes
        });
      } else {
        await updateDoc(postRef, {
          likes: arrayUnion({
            email,
            name
          })
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };
  const handleComment = async (postId: string) => {
    const content = commentInputs[postId];
    if (!content?.trim() || !user) return;
    try {
      const name = displayName || user.email?.split('@')[0] || 'Creator';
      const initials = name.substring(0, 2).toUpperCase();
      const postRef = doc(db, collectionName, postId);
      await updateDoc(postRef, {
        comments: arrayUnion({
          content,
          authorName: name,
          authorInitials: initials,
          createdAt: Timestamp.now()
        })
      });
      setCommentInputs(prev => ({
        ...prev,
        [postId]: ''
      }));
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };
  const handleDeleteComment = async (postId: string, commentToDelete: {
    content: string;
    authorName: string;
    authorInitials: string;
    createdAt: Timestamp | null;
  }) => {
    if (!user) return;
    const currentName = displayName || user.email?.split('@')[0] || 'Creator';
    if (commentToDelete.authorName !== currentName) return;
    try {
      const postRef = doc(db, collectionName, postId);
      const postSnap = await getDoc(postRef);
      if (postSnap.exists()) {
        const data = postSnap.data();
        const updatedComments = (data.comments || []).filter((c: any) => !(c.content === commentToDelete.content && c.authorName === commentToDelete.authorName && c.createdAt?.seconds === commentToDelete.createdAt?.seconds));
        await updateDoc(postRef, {
          comments: updatedComments
        });
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };
  return <div className="relative z-10 min-h-screen pt-28 pb-20 px-4 md:px-8" data-id="element-752" data-mp="1aaurajeo7mnz">
      <div className="max-w-6xl mx-auto" data-id="element-753" data-mp="1acz3rpqmxtzv">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-10 flex items-start justify-between" data-id="element-754" data-mp="1f9frwjxu48cy">
          <div data-id="element-755" data-mp="12yf053gbta8u">
            <div className="flex items-center gap-4 mb-2" data-id="element-756" data-mp="12xn7a0e45qm9">
              <button onClick={onBack} className="text-white/50 hover:text-white transition-colors font-['Montserrat'] text-sm" data-id="element-757" data-mp="y1eu2sjh87hq">
                ← Back
              </button>
              <h2 className="font-['Montserrat'] font-black text-3xl md:text-4xl text-white" data-id="element-758" data-mp="1qoj3d9plr7zb">
                {title}
              </h2>
            </div>
            <p className="text-white/50 font-['Montserrat']" data-id="element-759" data-mp="1gqldkhs3h34l">{subtitle}</p>
          </div>
          {user && <button onClick={onSignOut} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl backdrop-blur-xl bg-black/40 border border-white/10 text-white text-sm font-['Montserrat'] hover:border-red-500/30 hover:text-red-300 transition-all" data-id="element-760" data-mp="1br05nhuy5kfq">
              <LogOutIcon size={16} data-id="element-761" data-mp="q0qviayv15ya" />
              Sign Out
            </button>}
        </motion.div>
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-762" data-mp="v0wjnh5x8abm">
          {/* Left Column: Chat Feed */}
          <div className="flex-1 min-w-0" data-id="element-763" data-mp="h1ndzbfccylw">
            {user && canPost && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="backdrop-blur-md bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6 mb-8" data-id="element-764" data-mp="l15oredg7tb0">
                <textarea value={newPostContent} onChange={e => setNewPostContent(e.target.value)} style={{
              color: fontColor
            }} className={`w-full bg-black/20 border border-white/5 rounded-xl p-4 placeholder-white/40 focus:outline-none focus:border-purple-500/50 resize-none font-['Inter'] ${fontSize === 'sm' ? 'text-sm' : fontSize === 'lg' ? 'text-lg' : fontSize === 'xl' ? 'text-xl' : fontSize === '2xl' ? 'text-2xl' : 'text-base'}`} rows={3} placeholder="Share an update with the creators..." data-id="element-765" data-mp="13z8jm9sogkgq" />
                <div className="flex flex-wrap items-center gap-3 mt-4" data-id="element-766" data-mp="2y7u4rjeaq9mo">
                  <div className="flex items-center gap-2" data-id="element-767" data-mp="2t9karc4b5f2n">
                    <span className="text-white/40 text-xs font-['Montserrat']" data-id="element-768" data-mp="2tb3r12pyrq4f">
                      Color:
                    </span>
                    <div className="flex gap-1.5" data-id="element-769" data-mp="337qbhqedof5x">
                      {[{
                    color: '#ffffff',
                    label: 'White'
                  }, {
                    color: '#c084fc',
                    label: 'Purple'
                  }, {
                    color: '#22d3ee',
                    label: 'Cyan'
                  }, {
                    color: '#f472b6',
                    label: 'Pink'
                  }, {
                    color: '#facc15',
                    label: 'Yellow'
                  }, {
                    color: '#4ade80',
                    label: 'Green'
                  }, {
                    color: '#f87171',
                    label: 'Red'
                  }].map(c => <button key={c.color} type="button" onClick={() => setFontColor(c.color)} title={c.label} className={`w-6 h-6 rounded-full border-2 transition-all ${fontColor === c.color ? 'border-white scale-110' : 'border-white/20 hover:border-white/50'}`} style={{
                    backgroundColor: c.color
                  }} data-id="element-770" data-mp="38uxmofwwrvmq" />)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-771" data-mp="2ps5ijr61bstn">
                    <span className="text-white/40 text-xs font-['Montserrat']" data-id="element-772" data-mp="2psoumr907mzr">
                      Size:
                    </span>
                    <div className="flex gap-1" data-id="element-773" data-mp="2ftzizg7payry">
                      {[{
                    value: 'sm',
                    label: 'S'
                  }, {
                    value: 'base',
                    label: 'M'
                  }, {
                    value: 'lg',
                    label: 'L'
                  }, {
                    value: 'xl',
                    label: 'XL'
                  }, {
                    value: '2xl',
                    label: '2XL'
                  }].map(s => <button key={s.value} type="button" onClick={() => setFontSize(s.value)} className={`px-2 py-0.5 rounded text-xs font-['Montserrat'] font-bold transition-all ${fontSize === s.value ? 'bg-purple-500/30 border border-purple-500/50 text-purple-300' : 'bg-white/5 border border-white/10 text-white/40 hover:text-white/70'}`} data-id="element-774" data-mp="2xbejqx4e0l7m">
                          {s.label}
                        </button>)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-3" data-id="element-775" data-mp="k583hypev18m">
                  <button onClick={handlePost} disabled={isPosting || !newPostContent.trim()} className="rounded-lg bg-purple-600/80 hover:bg-purple-500 text-white font-semibold text-sm px-4 py-2" data-id="element-776" data-mp="k2vpfwmrxb0u">
                    {isPosting ? 'Posting...' : 'Post Update'}
                  </button>
                </div>
              </motion.div>}
            {/* Mobile Meeting Notes */}
            <div className="lg:hidden mb-8 backdrop-blur-md bg-black/40 border border-white/10 rounded-2xl overflow-hidden" data-id="element-777" data-mp="65yr7a4bsr9x">
              <button onClick={() => setIsNotesExpanded(!isNotesExpanded)} className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors" data-id="element-778" data-mp="b6cygpy3rx6w">
                <h3 className="font-['Montserrat'] font-bold text-xl text-white flex items-center gap-2" data-id="element-779" data-mp="1ri9wojmfo59l">
                  📝 Weekly Meeting Notes
                </h3>
                <span className="text-white/50" data-id="element-780" data-mp="1p5xxtwza27gg">
                  {isNotesExpanded ? '▲' : '▼'}
                </span>
              </button>
              <AnimatePresence data-id="element-781" data-mp="1z2kjgnzfh2jj">
                {isNotesExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="border-t border-white/10" data-id="element-782" data-mp="1yzomp2zbgjwo">
                    <div className="p-4 space-y-4" data-id="element-783" data-mp="2cu7qniuwrvh3">
                      {user && canPost ? <div className="flex flex-col gap-3" data-id="element-784" data-mp="2rqyhh3qinyv3">
                          <textarea value={meetingNotesText} onChange={e => setMeetingNotesText(e.target.value)} placeholder="Type your meeting notes here..." className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-purple-500 resize-y min-h-[120px] font-['Inter']" data-id="element-785" data-mp="2mseur143mj1o" />
                          <div className="flex items-center justify-between" data-id="element-786" data-mp="2mnbo9hkg4uj0">
                            <div className="text-white/40 text-xs font-['Montserrat']" data-id="element-787" data-mp="2rhpmdrqhtfrd">
                              {lastUpdatedBy && <>
                                  Last updated by {lastUpdatedBy}
                                  {lastUpdatedAt && ` on ${safeToDate(lastUpdatedAt)?.toLocaleDateString() ?? ''}`}
                                </>}
                            </div>
                            <button onClick={handleSaveMeetingNotes} disabled={isSavingNotes} className="px-4 py-2 bg-purple-600/80 hover:bg-purple-500 rounded-lg text-white text-sm font-semibold disabled:opacity-50 transition-colors" data-id="element-788" data-mp="1pml8hv6mz9e4">
                              {isSavingNotes ? 'Saving...' : 'Save Notes'}
                            </button>
                          </div>
                        </div> : <div className="bg-white/5 rounded-xl p-4 border border-white/5" data-id="element-789" data-mp="1ylgg9bpbwrjj">
                          <p className="text-white/80 text-sm whitespace-pre-wrap font-['Inter']" data-id="element-790" data-mp="1tmn9s38gm0sv">
                            {meetingNotesText || <span className="italic text-white/40" data-id="element-791" data-mp="n84jtlvwfb7e">
                                No meeting notes available.
                              </span>}
                          </p>
                          {lastUpdatedBy && <div className="mt-3 pt-3 border-t border-white/10 text-white/40 text-xs font-['Montserrat']" data-id="element-792" data-mp="2yt54nrgyv8jx">
                              Last updated by {lastUpdatedBy}
                              {lastUpdatedAt && ` on ${safeToDate(lastUpdatedAt)?.toLocaleDateString() ?? ''}`}
                            </div>}
                        </div>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
            {/* Feed */}
            <div className="space-y-6" data-id="element-793" data-mp="3rj3u3nz2p7p4">
              {posts.length === 0 ? <div className="text-center py-10 text-white/40 font-['Montserrat']" data-id="element-794" data-mp="1s5gbeat6w679">
                  No posts yet.
                </div> : posts.map((post, idx) => <motion.div key={post.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2 + idx * 0.1
            }} className={`backdrop-blur-md rounded-2xl p-5 md:p-6 ${post.pinned ? 'bg-black/40 border border-purple-500/50 shadow-[0_0_15px_rgba(168,85,247,0.2)]' : 'bg-black/40 border border-white/5'}`} data-id="element-795" data-mp="2wflb0jme58r" data-mpi={idx}>
                    {post.pinned && <div className="flex items-center gap-2 text-purple-400 text-xs font-bold font-['Montserrat'] mb-3 uppercase tracking-wider" data-id="element-796" data-mp="3pzc0hpe0fveq">
                        📌 Pinned Post
                      </div>}
                    <div className="flex items-center gap-3 mb-4" data-id="element-797" data-mp="10cmeey4q10tc">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-sm" data-id="element-798" data-mp="10efnek2t3x4i">
                        {post.authorInitials}
                      </div>
                      <div className="flex-1" data-id="element-799" data-mp="yvphbpfy4zsq">
                        <h4 className="text-white font-semibold font-['Montserrat']" data-id="element-800" data-mp="yx0l3p9e67ju">
                          {post.authorName}
                        </h4>
                        <p className="text-white/40 text-xs" data-id="element-801" data-mp="1qn5018edu2do">
                          {post.createdAt ? safeToDate(post.createdAt)?.toLocaleString() ?? 'Just now' : 'Just now'}
                        </p>
                      </div>
                    </div>
                    {post.content && <p className={`leading-relaxed mb-4 whitespace-pre-wrap ${post.fontSize === 'sm' ? 'text-sm' : post.fontSize === 'lg' ? 'text-lg' : post.fontSize === 'xl' ? 'text-xl' : post.fontSize === '2xl' ? 'text-2xl' : 'text-sm md:text-base'}`} style={{
                color: post.fontColor || 'rgba(255,255,255,0.8)'
              }} data-id="element-802" data-mp="29kpncb1i0sw6">
                        {post.content}
                      </p>}
                    <div className="mb-4 flex items-center gap-3" data-id="element-803" data-mp="3tjl4ema47801">
                      <button onClick={() => user && handleLike(post)} className={`flex items-center gap-1.5 text-sm font-['Montserrat'] transition-all ${!user ? 'text-white/30 cursor-default' : post.likes?.some(l => l.email === user.email) ? 'text-pink-400' : 'text-white/40 hover:text-pink-400'}`} data-id="element-804" data-mp="2wultt0m25u4o">
                        <span className="text-lg" data-id="element-805" data-mp="l49rjp4rzd0x">
                          {user && post.likes?.some(l => l.email === user.email) ? '❤️' : '🤍'}
                        </span>
                        <span data-id="element-806" data-mp="2akn6hx8pi282">{post.likes?.length || 0}</span>
                      </button>
                      {(post.likes?.length || 0) > 0 && <p className="text-white/30 text-xs font-['Montserrat']" data-id="element-807" data-mp="20v3lom1fnyak">
                          Liked by {post.likes?.map(l => l.name).join(', ')}
                        </p>}
                    </div>
                    <div className="border-t border-white/10 pt-4 space-y-4" data-id="element-808" data-mp="1rwi7t46wp7g4">
                      {post.comments?.map((comment, cIdx) => <div key={cIdx} className="flex gap-3" data-id="element-809" data-mp="1hwrev6xzv0i5">
                          <div className="w-8 h-8 rounded-full bg-pink-500/50 flex-shrink-0 flex items-center justify-center text-white text-xs font-bold" data-id="element-810" data-mp="1hwzqywmam1sa">
                            {comment.authorInitials}
                          </div>
                          <div className="bg-white/5 rounded-2xl rounded-tl-none p-3 flex-1" data-id="element-811" data-mp="37bxitriay8hr">
                            <div className="flex justify-between items-baseline mb-1" data-id="element-812" data-mp="15mry5uxi4ovr">
                              <h5 className="text-white/90 text-sm font-semibold" data-id="element-813" data-mp="15kp0qpu0rth0">
                                {comment.authorName}
                              </h5>
                              <div className="flex items-center gap-2" data-id="element-814" data-mp="vq5e38d021us">
                                <span className="text-white/30 text-[10px]" data-id="element-815" data-mp="10ogmdennc4aw">
                                  {comment.createdAt ? safeToDate(comment.createdAt)?.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          }) ?? '' : ''}
                                </span>
                                {user && comment.authorName === (displayName || user.email?.split('@')[0] || '') && <button onClick={() => handleDeleteComment(post.id, comment)} className="text-white/20 hover:text-red-400 transition-colors text-xs" title="Delete comment" data-id="element-816" data-mp="3rl0725gb2kuh">
                                      ✕
                                    </button>}
                              </div>
                            </div>
                            <p className="text-white/70 text-sm whitespace-pre-wrap" data-id="element-817" data-mp="3irwzuew9748a">
                              {comment.content}
                            </p>
                          </div>
                        </div>)}
                      {user && <div className="flex gap-3 items-center mt-4" data-id="element-818" data-mp="1teqlgl6xvqvl">
                          <div className="w-8 h-8 rounded-full bg-white/10 flex-shrink-0 flex items-center justify-center text-white/50 text-xs font-bold" data-id="element-819" data-mp="1xdzflr5w5qro">
                            {displayName ? displayName.substring(0, 2).toUpperCase() : user.email?.substring(0, 2).toUpperCase()}
                          </div>
                          <div className="flex-1 flex gap-2" data-id="element-820" data-mp="2h2hw7kctzvur">
                            <input type="text" value={commentInputs[post.id] || ''} onChange={e => setCommentInputs(prev => ({
                      ...prev,
                      [post.id]: e.target.value
                    }))} onKeyDown={e => e.key === 'Enter' && handleComment(post.id)} placeholder="Add a comment..." className="flex-1 bg-transparent border-none text-white text-sm focus:outline-none placeholder-white/30" data-id="element-821" data-mp="2h0x4e2hwir0l" />
                            <button onClick={() => handleComment(post.id)} disabled={!commentInputs[post.id]?.trim()} className="text-purple-400 hover:text-purple-300" data-id="element-822" data-mp="9ex0f9vr3m8f">
                              Post
                            </button>
                          </div>
                        </div>}
                    </div>
                  </motion.div>)}
            </div>
          </div>
          {/* Right Column: Meeting Notes Sidebar */}
          <div className="hidden lg:block w-80 flex-shrink-0" data-id="element-823" data-mp="3fcdk0xfztthg">
            <div className="sticky top-28 backdrop-blur-md bg-black/40 border border-white/10 rounded-2xl overflow-hidden" data-id="element-824" data-mp="3fcwr1j43uxgp">
              <div className="p-4 bg-white/5 border-b border-white/10" data-id="element-825" data-mp="3k940vmzmgt3x">
                <h3 className="font-['Montserrat'] font-bold text-lg text-white flex items-center gap-2" data-id="element-826" data-mp="35dnz0vssvswz">
                  📝 Weekly Meeting Notes
                </h3>
              </div>
              <div className="p-4 space-y-4" data-id="element-827" data-mp="2wl40dvcidoc5">
                {user && canPost ? <div className="flex flex-col gap-3" data-id="element-828" data-mp="31dnmjgp9hrsk">
                    <textarea value={meetingNotesText} onChange={e => setMeetingNotesText(e.target.value)} placeholder="Type your meeting notes here..." className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-purple-500 resize-y min-h-[200px] font-['Inter']" data-id="element-829" data-mp="2mnpugpo7ohy3" />
                    <button onClick={handleSaveMeetingNotes} disabled={isSavingNotes} className="w-full px-4 py-2 bg-purple-600/80 hover:bg-purple-500 rounded-lg text-white text-sm font-semibold disabled:opacity-50 transition-colors" data-id="element-830" data-mp="1z1436dc9wpsf">
                      {isSavingNotes ? 'Saving...' : 'Save Notes'}
                    </button>
                    {lastUpdatedBy && <div className="text-white/40 text-xs font-['Montserrat'] text-center mt-2" data-id="element-831" data-mp="2d0c7fknamo3h">
                        Last updated by {lastUpdatedBy}
                        <br data-id="element-832" data-mp="2qqpap9p8pemh" />
                        {lastUpdatedAt && (safeToDate(lastUpdatedAt)?.toLocaleString() ?? '')}
                      </div>}
                  </div> : <div className="bg-white/5 rounded-xl p-4 border border-white/5" data-id="element-833" data-mp="2vr3lv0m514oq">
                    <p className="text-white/80 text-sm whitespace-pre-wrap font-['Inter']" data-id="element-834" data-mp="6xn2ilpur6xq">
                      {meetingNotesText || <span className="italic text-white/40" data-id="element-835" data-mp="ay6wjgf4pufn">
                          No meeting notes available.
                        </span>}
                    </p>
                    {lastUpdatedBy && <div className="mt-4 pt-3 border-t border-white/10 text-white/40 text-xs font-['Montserrat']" data-id="element-836" data-mp="kvbm7q86o817">
                        Last updated by {lastUpdatedBy}
                        <br data-id="element-837" data-mp="3oeo58k9fdssi" />
                        {lastUpdatedAt && (safeToDate(lastUpdatedAt)?.toLocaleString() ?? '')}
                      </div>}
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// ── Public Pages ────────────────────────────────────────────────
function HomePage({
  onNavigate
}: {
  onNavigate: (page: PageId) => void;
}) {
  return <div className="relative z-10 min-h-screen flex flex-col items-center justify-end px-4 pb-20" data-id="element-838" data-mp="2y32wmd5kb8xj">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1
    }} className="text-center max-w-4xl mx-auto w-full" data-id="element-839" data-mp="2t62qk28pq66f">
        <ActionButtons currentPage="home" onNavigate={onNavigate} data-id="element-840" data-mp="2f5ryivvo2ljo" />
      </motion.div>
    </div>;
}
function AboutPage() {
  return <div className="relative z-10 min-h-screen pt-32 pb-20 px-4 md:px-8" data-id="element-841" data-mp="2acyt05sdj96j">
      <div className="max-w-4xl mx-auto" data-id="element-842" data-mp="25enli1pjxarh">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="backdrop-blur-xl bg-black/30 border border-white/10 rounded-3xl p-6 md:p-8 mb-8 text-center shadow-lg" data-id="element-843" data-mp="25dcezu4q1owo">
          <h2 className="font-['Montserrat'] font-black text-4xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-b from-white to-yellow-300" data-id="element-844" data-mp="1ri1n8plrxjwj">
            💜 About TWM
          </h2>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="backdrop-blur-xl bg-black/10 border border-white/10 rounded-3xl p-8 md:p-12 text-left space-y-6" data-id="element-845" data-mp="1aqg7zabm41po">
          <p className="text-white/90 font-['Inter'] text-xl font-semibold leading-relaxed" data-id="element-846" data-mp="n1dri5spsewr">
            Welcome! 👋 I'm Mandy, and I'm so glad you're here.
          </p>
          <p className="text-white/80 font-['Inter'] text-lg leading-relaxed" data-id="element-847" data-mp="wzbdsnr0k1mq">
            I created TWM because I saw too many creators jumping into TikTok
            LIVE without anyone teaching them the basics. So many people have
            the passion to succeed—they just need someone willing to show them
            how.
          </p>
          <p className="text-white/90 font-['Inter'] text-lg font-semibold leading-relaxed" data-id="element-848" data-mp="3sybwxhqc9n9l">
            ✨ That's where I come in.
          </p>
          <p className="text-white/80 font-['Inter'] text-lg leading-relaxed" data-id="element-849" data-mp="3j5o2uuqqtxek">
            This isn't an agency that promises overnight success, huge gift
            throwers, or instant fame. Instead, it's a place where you'll learn
            the skills that actually matter.
          </p>
          <div data-id="element-850" data-mp="35656ez7aoai3">
            <p className="text-white/90 font-['Inter'] text-lg font-semibold mb-3" data-id="element-851" data-mp="355lo3hdqfk3j">
              Together, we'll work on things like:
            </p>
            <ul className="space-y-2 text-white/80 font-['Inter'] text-lg leading-relaxed" data-id="element-852" data-mp="309erif787ky9">
              <li className="flex items-start gap-2" data-id="element-853" data-mp="2valawdwn7dtv">
                <span className="mt-1" data-id="element-854" data-mp="yf5s6bsmd6ii">🎥</span>
                <span data-id="element-855" data-mp="ydum25bpbmo8">Setting up your livestream and equipment</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-856" data-mp="tf17saolbrn0">
                <span className="mt-1" data-id="element-857" data-mp="2nx2pqh5oiiul">💻</span>
                <span data-id="element-858" data-mp="2nv9f6qry3ehb">Building a streaming setup that works for you</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-859" data-mp="2sq5iwo6jnqa9">
                <span className="mt-1" data-id="element-860" data-mp="2dzy027cmk591">🎯</span>
                <span data-id="element-861" data-mp="2dx21cgsydcjj">Finding your niche and content style</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-862" data-mp="2iyrfi5bh26bu">
                <span className="mt-1" data-id="element-863" data-mp="36nan0imp6f2v">💬</span>
                <span data-id="element-864" data-mp="36olmw7d0v2op">Keeping conversations flowing on LIVE</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-865" data-mp="3blls6g21s1er">
                <span className="mt-1" data-id="element-866" data-mp="2wpwf57euaym6">📈</span>
                <span data-id="element-867" data-mp="2wp4irudf4wuu">Growing your community the right way</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-868" data-mp="31npilsordeir">
                <span className="mt-1" data-id="element-869" data-mp="1lahadhge32gi">🧠</span>
                <span data-id="element-870" data-mp="1l9powzgial3y">
                  Understanding what works—and what doesn't—on TikTok LIVE
                </span>
              </li>
            </ul>
          </div>
          <p className="text-white/80 font-['Inter'] text-lg leading-relaxed" data-id="element-871" data-mp="8bfojw3jbyfl">
            But our journey doesn't stop with TikTok.
          </p>
          <p className="text-white/80 font-['Inter'] text-lg leading-relaxed" data-id="element-872" data-mp="3cmbu5w2obgh">
            🌎 I believe financial freedom comes from creating multiple streams
            of income, not relying on a single platform. That's why I also love
            teaching creators about entrepreneurship, side hustles, artificial
            intelligence, digital business, and other ways to build a life
            that's bigger than social media.
          </p>
          <p className="text-white/90 font-['Inter'] text-lg font-semibold leading-relaxed" data-id="element-873" data-mp="3ujp3eyftojr8">
            💡 My goal is simple:
          </p>
          <p className="text-white/80 font-['Inter'] text-lg leading-relaxed" data-id="element-874" data-mp="3pqw08f005jh9">
            I want to give you the knowledge, confidence, and support to build
            something you're proud of. Whether you stay with TWM for years or
            eventually move on to bigger opportunities, I want you to leave
            stronger, smarter, and more prepared than when you started.
          </p>
          <p className="text-white/90 font-['Inter'] text-lg font-semibold leading-relaxed" data-id="element-875" data-mp="3fq4mnc6kmkn7">
            ❤️ Around here, we celebrate progress over perfection.
          </p>
          <p className="text-white/80 font-['Inter'] text-lg leading-relaxed" data-id="element-876" data-mp="3pgg503r04wdm">
            Because every creator starts somewhere—and no one should have to
            figure it out alone.
          </p>
          <p className="text-white/80 font-['Inter'] text-lg leading-relaxed" data-id="element-877" data-mp="29nk6q2vbiis">
            Welcome to TWM.
          </p>
          <p className="text-white/90 font-['Inter'] text-xl font-bold leading-relaxed text-yellow-300" data-id="element-878" data-mp="7ab7p1rw1h89">
            Let's build your future—together. 🚀
          </p>
        </motion.div>
      </div>
    </div>;
}
function WhyGSFPage() {
  const reasons = [{
    title: '✨ We actually help you grow',
    desc: 'We guide you through content, live strategy, and finding your niche so you can build something sustainable.'
  }, {
    title: "✨ You don't have to figure it out alone",
    desc: "We've got experienced creators who've been doing this for years, and they're here to help you."
  }, {
    title: '✨ Community over competition',
    desc: "We don't gatekeep. We share knowledge, support each other, and celebrate wins together."
  }, {
    title: '✨ Multiple niches, one team',
    desc: 'Music artists, streamers, life coaches, DJs, travelers — we bring different worlds together.'
  }, {
    title: '✨ No cost to join',
    desc: 'You get access to knowledge, support, and opportunity without paying to be here.'
  }, {
    title: '✨ Real people. Real growth.',
    desc: 'No fake promises. Just consistency, support, and results over time.'
  }];
  return <div className="relative z-10 min-h-screen pt-32 pb-20 px-4 md:px-8" data-id="element-879" data-mp="z3552fk9qomp">
      <div className="max-w-5xl mx-auto" data-id="element-880" data-mp="z59hjlw8gvyl">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="font-['Montserrat'] font-black text-4xl md:text-6xl text-center text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-4" data-id="element-881" data-mp="3n8kbacwp0737">
          💜 Why Choose TWM?
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className="text-center text-white/80 font-['Inter'] text-xl mb-4" data-id="element-882" data-mp="33kl0j224ozxs">
          Because this isn't just another agency.
        </motion.p>
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} className="text-center text-white/70 font-['Inter'] text-lg mb-12" data-id="element-883" data-mp="2kmijpajqi9b2">
          At TWM, you're not just a number — you're part of something real.
        </motion.p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-884" data-mp="2fy94arq3e54o">
          {reasons.map((r, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3 + i * 0.1
        }} className="backdrop-blur-xl bg-black/10 border border-white/10 rounded-2xl p-8 hover:border-purple-500/30 transition-all" data-id="element-885" data-mp="2ayd8hfrjtk07" data-mpi={i}>
              <h3 className="font-['Montserrat'] font-bold text-2xl text-white mb-4" data-id="element-886" data-mp="2trgwollzz4nf">
                {r.title}
              </h3>
              <p className="text-white/70 font-['Inter'] leading-relaxed" data-id="element-887" data-mp="33o3d8y7aj9jn">
                {r.desc}
              </p>
            </motion.div>)}
        </div>
      </div>
    </div>;
}
function ServicesPage() {
  const services = [{
    emoji: '🎥',
    title: 'Live Streaming Support',
    bullets: ['Learn how to run engaging lives', 'Understand gifting, engagement, and retention', 'Build real connections with your audience']
  }, {
    emoji: '📈',
    title: 'Growth Strategy',
    bullets: ['Help finding your niche', 'Content direction based on YOUR strengths', 'Personalized guidance (not cookie-cutter advice)']
  }, {
    emoji: '🤝',
    title: 'Community & Networking',
    bullets: ['Connect with other creators', 'Collaborate and cross-grow', 'Be part of a supportive environment']
  }, {
    emoji: '🎯',
    title: 'Creator Development',
    bullets: ['Confidence building on camera', 'Branding and identity help', 'Leveling up your presence']
  }, {
    emoji: '💡',
    title: 'Monetization Guidance',
    bullets: ['Understanding how to earn through lives', 'Building consistency for income growth', 'Learning what actually converts']
  }, {
    emoji: '🌍',
    title: 'Team Opportunities',
    bullets: ['Grow into leadership roles', 'Build your own team under TWM', 'Expand beyond just being a creator']
  }];
  return <div className="relative z-10 min-h-screen pt-32 pb-20 px-4 md:px-8" data-id="element-888" data-mp="2hc7k5vozrzm9">
      <div className="max-w-5xl mx-auto" data-id="element-889" data-mp="2dbdscr8fi0o3">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="font-['Montserrat'] font-black text-4xl md:text-6xl text-center text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-4" data-id="element-890" data-mp="2da2s31e6zb1q">
          🚀 What We Offer
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className="text-center text-white/80 font-['Inter'] text-xl mb-12" data-id="element-891" data-mp="188rvuus209zm">
          We help creators grow, connect, and turn their content into something
          sustainable.
        </motion.p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-892" data-mp="kla614scnlza">
          {services.map((s, i) => <motion.div key={i} initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2 + i * 0.1
        }} className="backdrop-blur-xl bg-black/10 border border-white/10 rounded-2xl p-8 hover:border-purple-500/30 transition-all" data-id="element-893" data-mp="ycow1vkocnfu" data-mpi={i}>
              <div className="text-4xl mb-4" data-id="element-894" data-mp="2hauuuzta1dcs">{s.emoji}</div>
              <h3 className="font-['Montserrat'] font-bold text-xl text-white mb-4" data-id="element-895" data-mp="2hcecatqo5ug5">
                {s.title}
              </h3>
              <ul className="space-y-2" data-id="element-896" data-mp="28isxxbif2f79">
                {s.bullets.map((b, idx) => <li key={idx} className="text-white/70 font-['Inter'] leading-relaxed flex items-start gap-2" data-id="element-897" data-mp="28gf04uwcqow0" data-mpi={idx}>
                    <span className="text-purple-400 mt-0.5" data-id="element-898" data-mp="309627m0pv7t6">•</span>
                    <span data-id="element-899" data-mp="2m94p9876g9xc">{b}</span>
                  </li>)}
              </ul>
            </motion.div>)}
        </div>
        {/* Tagline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} className="text-center mt-16" data-id="element-900" data-mp="1av0zyfbjb9ym">
          <p className="font-['Montserrat'] font-bold text-xl md:text-2xl text-white/60 italic leading-relaxed" data-id="element-901" data-mp="vtu1orys2l68">
            "Not just content. Not just lives.
            <br data-id="element-902" data-mp="myec4n522s6g" />A community built to help you win."
          </p>
        </motion.div>
      </div>
    </div>;
}
function ContactPage({
  onNavigate
}: {
  onNavigate: (page: PageId) => void;
}) {
  return <div className="relative z-10 min-h-screen pt-32 pb-20 px-4 md:px-8" data-id="element-903" data-mp="d2b4x2vck3cn">
      <div className="max-w-2xl mx-auto text-center" data-id="element-904" data-mp="825fltcjusxq">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="font-['Montserrat'] font-black text-4xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-b from-white to-purple-300 mb-6" data-id="element-905" data-mp="84rnxv79lch0">
          Join TWM Agency
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15
      }} className="text-white/70 font-['Inter'] text-lg mb-8" data-id="element-906" data-mp="1tgjbvp3mw436">
          Ready to grow with a community that actually supports you? Tap below
          to join.
        </motion.p>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="mb-10" data-id="element-907" data-mp="2fpztz6cci7fq">
          <a href="https://www.tiktok.com/t/ZTUegUbeP/" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-lg font-['Montserrat'] shadow-[0_0_20px_rgba(168,85,247,0.4)] hover:shadow-[0_0_30px_rgba(168,85,247,0.6)] transition-all hover:scale-105" data-id="element-908" data-mp="1rxayn2skp84p">
            Join TWM Agency
          </a>
        </motion.div>
        {/* Agency Transfer Warning */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="backdrop-blur-xl bg-black/10 border border-white/10 rounded-3xl p-8 md:p-10 text-left" data-id="element-909" data-mp="31179d1j9nlwq">
          <p className="text-white/90 font-['Inter'] text-base leading-relaxed mb-4" data-id="element-910" data-mp="3jwxakmjdke9k">
            <span className="text-yellow-400 font-bold" data-id="element-911" data-mp="2i0cx99fsxgn">
              ⚠️ Already in another agency?
            </span>{' '}
            You'll need to request to leave your current agency first. After
            leaving, there's a{' '}
            <span className="font-bold" data-id="element-912" data-mp="biolvpjz47mo">30-60 day waiting period</span> before
            you can apply to a new one.
          </p>
          <p className="text-white/90 font-['Inter'] text-base leading-relaxed" data-id="element-913" data-mp="7eqrfs5lfkzy">
            However, if you're already in the process of leaving and want to
            come over to TWM —{' '}
            <span className="text-white/90 underline" data-id="element-914" data-mp="v1kt9pq6m6dn">
              reach out to any of our agency agents
            </span>
            . We can start taking you under our wing immediately. We don't
            believe in turning our backs on people.
          </p>
        </motion.div>
      </div>
    </div>;
}
// ── Agency Page (self-contained standalone app) ─────────────────
export function AgencyPage() {
  const [currentPage, setCurrentPage] = useState<PageId>('home');
  const [agentUser, setAgentUser] = useState<User | null>(null);
  const [agentDisplayName, setAgentDisplayName] = useState('');
  const [agentRole, setAgentRole] = useState('');
  const [creatorUser, setCreatorUser] = useState<User | null>(null);
  const [creatorDisplayName, setCreatorDisplayName] = useState('');
  const [creatorRole, setCreatorRole] = useState('');
  useEffect(() => {
    const unsubscribeAgent = onAuthStateChanged(auth, async user => {
      setAgentUser(user);
      if (user) {
        try {
          const docSnap = await getDoc(doc(db, 'users', user.uid));
          if (docSnap.exists()) {
            const data = docSnap.data();
            setAgentDisplayName(data?.Name || data?.name || user.email?.split('@')[0] || '');
            setAgentRole(data?.role || 'creator');
          } else {
            setAgentDisplayName(user.email?.split('@')[0] || '');
          }
        } catch (e) {
          setAgentDisplayName(user.email?.split('@')[0] || '');
        }
      }
    });
    const unsubscribeCreator = onAuthStateChanged(creatorAuth, async user => {
      setCreatorUser(user);
      if (user) {
        try {
          const docSnap = await getDoc(doc(db, 'users', user.uid));
          if (docSnap.exists()) {
            const data = docSnap.data();
            setCreatorDisplayName(data?.Name || data?.name || user.email?.split('@')[0] || '');
            setCreatorRole(data?.role || 'creator');
          } else {
            setCreatorDisplayName(user.email?.split('@')[0] || '');
          }
        } catch (e) {
          setCreatorDisplayName(user.email?.split('@')[0] || '');
        }
      }
    });
    return () => {
      unsubscribeAgent();
      unsubscribeCreator();
    };
  }, []);
  const handleAgentLogin = (displayName: string, role: string) => {
    setAgentDisplayName(displayName);
    setAgentRole(role);
    setCurrentPage('portal-hub');
  };
  const handleCreatorLogin = (displayName: string, role: string) => {
    setCreatorDisplayName(displayName);
    setCreatorRole(role);
    setCurrentPage('creator-hub');
  };
  const handleAgentSignOut = async () => {
    await signOut(auth);
    setAgentUser(null);
    setAgentDisplayName('');
    setAgentRole('');
    setCurrentPage('home');
  };
  const handleCreatorSignOut = async () => {
    await signOut(creatorAuth);
    setCreatorUser(null);
    setCreatorDisplayName('');
    setCreatorRole('');
    setCurrentPage('home');
  };
  return <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden font-['Inter']" data-id="element-915" data-mp="1erf2y6fg0se2">
      {/* Video Background */}
      <div className="fixed top-0 left-0 right-0 bottom-0 z-0 overflow-hidden bg-black" data-id="element-916" data-mp="1tixd3nlmfspi">
        <video key={currentPage === 'about' || currentPage === 'services' ? 'about-services-bg' : currentPage === 'why' || currentPage === 'contact' ? 'why-contact-bg' : 'default-bg'} src={currentPage === 'about' || currentPage === 'services' ? 'https://cdn.magicpatterns.com/uploads/nammQP7j6MJLaMLWGs6GLh/lv_0_20260708191835.mp4' : currentPage === 'why' || currentPage === 'contact' ? 'https://cdn.magicpatterns.com/uploads/xbN3sK1Zu6eUiHncMG9vjD/lv_0_20260708193050.mp4' : 'https://cdn.magicpatterns.com/uploads/wcQJLfXxLV8mRAgLmyPhMp/20260625_175149131.mp4'} autoPlay loop muted playsInline className={`absolute inset-0 w-full h-full object-contain md:object-cover opacity-80 ${['about', 'why', 'services', 'contact'].includes(currentPage) ? 'object-center md:object-[center_65%]' : 'object-center'}`} data-id="element-917" data-mp="1oijf6ydqhz57" />
      </div>
      {/* Only show navbar on public pages */}
      {!['agent-login', 'creator-login', 'portal-hub', 'creator-hub', 'portal', 'creator-portal'].includes(currentPage) && <Navbar currentPage={currentPage} onNavigate={setCurrentPage} data-id="element-918" data-mp="2xqcnzuvr63om" />}
      <AnimatePresence mode="wait" data-id="element-919" data-mp="32oehzaupvnev">
        {currentPage === 'home' && <motion.div key="home" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-920" data-mp="3qf1s82s3up4l">
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-921" data-mp="1vkjw1bp08x2v" />
            <HomePage onNavigate={setCurrentPage} data-id="element-922" data-mp="1vmmkhraiei5h" />
          </motion.div>}
        {currentPage === 'about' && <motion.div key="about" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-923" data-mp="1luqlme0lmo07">
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-924" data-mp="1buhn2tj4wqhn" />
            <AboutPage data-id="element-925" data-mp="xz5qeglf4py3" />
          </motion.div>}
        {currentPage === 'why' && <motion.div key="why" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-926" data-mp="12we9cjee4y0e">
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-927" data-mp="lnlhhlm337h0" />
            <WhyGSFPage data-id="element-928" data-mp="goid83dxh5a3" />
          </motion.div>}
        {currentPage === 'services' && <motion.div key="services" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-929" data-mp="vcvcb98bb48t">
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-930" data-mp="zeqa43r1azhu" />
            <ServicesPage data-id="element-931" data-mp="1ebh8c9oz255v" />
          </motion.div>}
        {currentPage === 'contact' && <motion.div key="contact" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-932" data-mp="19cedfi2phfbm">
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-933" data-mp="3b63yceqvfni3" />
            <ContactPage onNavigate={setCurrentPage} data-id="element-934" data-mp="4obsynto1omr" />
          </motion.div>}
        {/* Login Pages */}
        {currentPage === 'agent-login' && <motion.div key="agent-login" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-935" data-mp="dmxjp69ak9ym">
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-936" data-mp="rmgfr0otvuuk" />
            <div className="absolute top-6 left-6 z-50" data-id="element-937" data-mp="wdopjxu2li89">
              <button onClick={() => setCurrentPage('home')} className="text-white/50 hover:text-white font-['Montserrat'] text-sm" data-id="element-938" data-mp="we889grjoanc">
                ← Back to Home
              </button>
            </div>
            <div className="relative z-10 min-h-screen flex items-center justify-center px-4" data-id="element-939" data-mp="1bapi23k35h7s">
              <div className="max-w-md w-full" data-id="element-940" data-mp="2vsqcebw9v65d">
                <h2 className="font-['Montserrat'] font-black text-4xl text-center text-white mb-12" data-id="element-941" data-mp="2vuky3sfdb2ai">
                  Agent Portal Login
                </h2>
                <LoginPage title="Agent Portal Login" type="agent" onLogin={handleAgentLogin} data-id="element-942" data-mp="2luaki6pf7sut" />
              </div>
            </div>
          </motion.div>}
        {currentPage === 'creator-login' && <motion.div key="creator-login" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-943" data-mp="39keju1vkt6g0">
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-944" data-mp="2zsqymipm4lmz" />
            <div className="absolute top-6 left-6 z-50" data-id="element-945" data-mp="15wbs05mxqwe">
              <button onClick={() => setCurrentPage('home')} className="text-white/50 hover:text-white font-['Montserrat'] text-sm" data-id="element-946" data-mp="3sj0lbhv08zkg">
                ← Back to Home
              </button>
            </div>
            <div className="relative z-10 min-h-screen flex items-center justify-center px-4" data-id="element-947" data-mp="3hy9cm2xfh7ta">
              <div className="max-w-md w-full" data-id="element-948" data-mp="335656odjwaga">
                <h2 className="font-['Montserrat'] font-black text-4xl text-center text-white mb-12" data-id="element-949" data-mp="381d0zoc8g5ki">
                  Creator Portal Login
                </h2>
                <LoginPage title="Creator Portal Login" type="creator" onLogin={handleCreatorLogin} data-id="element-950" data-mp="3vu3bdt4hmgso" />
              </div>
            </div>
          </motion.div>}
        {/* Portal Hubs */}
        {currentPage === 'portal-hub' && <motion.div key="portal-hub" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-951" data-mp="ilh09j8ejdd9">
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-952" data-mp="27zzv2f8vyehd" />
            <div className="relative z-10 min-h-screen flex items-center justify-center px-4" data-id="element-953" data-mp="2315bhb33glw7">
              <div className="absolute top-6 left-6 z-50" data-id="element-954" data-mp="2hrcmx6uop4ne">
                <button onClick={handleAgentSignOut} className="text-white/50 hover:text-white font-['Montserrat'] text-sm" data-id="element-955" data-mp="2cssz10wj5ise">
                  ← Sign Out
                </button>
              </div>
              <div className="max-w-4xl w-full" data-id="element-956" data-mp="1k6qajfx82j5q">
                <h2 className="font-['Montserrat'] font-black text-4xl text-center text-white mb-12" data-id="element-957" data-mp="1y57tl0c6n7hb">
                  Select Portal
                </h2>
                <div className="max-w-xl mx-auto" data-id="element-958" data-mp="1u4xmfkcnmb0v">
                  <button onClick={() => setCurrentPage('portal')} className="w-full backdrop-blur-xl bg-black/10 border border-white/10 rounded-3xl p-10 hover:border-purple-500/50 hover:bg-white/5 transition-all group text-left" data-id="element-959" data-mp="39i0js6pbrnv9">
                    <h3 className="font-['Montserrat'] font-black text-3xl text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-4 group-hover:scale-105 transition-transform origin-left" data-id="element-960" data-mp="274uktdlbmqv2">
                      TWM Live
                    </h3>
                    <p className="text-white/50 font-['Inter']" data-id="element-961" data-mp="1jgl2wmr8an97">
                      Access the main TWM agency portal and communications.
                    </p>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>}
        {currentPage === 'creator-hub' && <motion.div key="creator-hub" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-962" data-mp="1x7zq75s65am2">
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-963" data-mp="x8fnt3451nmp" />
            <div className="relative z-10 min-h-screen flex items-center justify-center px-4" data-id="element-964" data-mp="s8t88n0u7vaq">
              <div className="absolute top-6 left-6 z-50" data-id="element-965" data-mp="o77oqitssirk">
                <button onClick={handleCreatorSignOut} className="text-white/50 hover:text-white font-['Montserrat'] text-sm" data-id="element-966" data-mp="o5oaexrpchsf">
                  ← Sign Out
                </button>
              </div>
              <div className="max-w-4xl w-full" data-id="element-967" data-mp="9htg5cy1q5d8">
                <h2 className="font-['Montserrat'] font-black text-4xl text-center text-white mb-12" data-id="element-968" data-mp="9h0hkz98qm66">
                  Select Portal
                </h2>
                <div className="max-w-xl mx-auto" data-id="element-969" data-mp="i6npgnao9wb">
                  <button onClick={() => setCurrentPage('creator-portal')} className="w-full backdrop-blur-xl bg-black/10 border border-white/10 rounded-3xl p-10 hover:border-purple-500/50 hover:bg-white/5 transition-all group text-left" data-id="element-970" data-mp="fk6epalqg0w">
                    <h3 className="font-['Montserrat'] font-black text-3xl text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-4 group-hover:scale-105 transition-transform origin-left" data-id="element-971" data-mp="1ee2igqgy7psy">
                      TWM Creator Portal
                    </h3>
                    <p className="text-white/50 font-['Inter']" data-id="element-972" data-mp="1ad8y272pst4t">
                      Access the main TWM creator updates and resources.
                    </p>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>}
        {/* Portals */}
        {currentPage === 'portal' && <motion.div key="portal" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-973" data-mp="qqklmgitu1j4">
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-974" data-mp="hrz5st4qp0m1" />
            <PortalPage user={agentUser} displayName={agentDisplayName} userRole={agentRole} title="TWM Live Portal" subtitle="Internal communications and updates" collectionName="posts" notesCollectionName="meeting_notes" onSignOut={handleAgentSignOut} onBack={() => setCurrentPage('portal-hub')} data-id="element-975" data-mp="3ua0jfgnov3i3" />
          </motion.div>}
        {currentPage === 'creator-portal' && <motion.div key="creator-portal" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="relative" data-id="element-976" data-mp="c1szm4mmsdcr">
            <div className="fixed inset-0 bg-black/25 pointer-events-none z-0" data-id="element-977" data-mp="ztpzcxct3exi" />
            <CreatorPortalPage user={creatorUser} displayName={creatorDisplayName} userRole={creatorRole} title="TWM Creator Portal" subtitle="Updates and resources for TWM creators" collectionName="posts" notesCollectionName="meeting_notes" onSignOut={handleCreatorSignOut} onBack={() => setCurrentPage('creator-hub')} data-id="element-978" data-mp="vt66w736gzhp" />
          </motion.div>}
      </AnimatePresence>
      <footer className="relative z-10 w-full py-3 text-center bg-[#3a1a30]" data-id="element-979" data-mp="1e90c7th11m37">
        <p className="text-[#e8c8d4] text-[10px] md:text-xs font-['Montserrat'] tracking-[0.25em] uppercase" data-id="element-980" data-mp="19g7897tduhs6">
          Made with <span className="text-pink-400" data-id="element-981" data-mp="14i5oeko4uefn">♥</span> and grit by
          Trippin' with Mandy™
        </p>
      </footer>
    </div>;
}